import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, AlertTriangle, Upload, Plus, X, Search, ArrowLeft, Sparkles } from 'lucide-react';
import { DateTimePicker } from '../../components/ui/date-picker';
import { useAlert } from '../../context/AlertContext';
import { useSite } from '../../context/SiteContext';
import { useAuth } from '../../context/AuthContext';
import {
  nowInTenantInput, toTenantLocalInput, tenantInputToInstant,
  addHoursInTenantInput, toWallClockInput,
} from '../../utils/tenantTime';
import { getGasReadingWarnings, isGasReadingFieldUnsafe } from '../../utils/ptwValidation';
import {
  stripDigits, sanitizeMobile, MOBILE_DIGITS, toNationalDigits,
  isValidContactNumber, toContactSubmitValue, mobileRules,
} from '../../utils/inputSanitizers';
import PhoneInput, { defaultPhoneCountry } from '../../components/PhoneInput';
import { formatRoleName } from '../../utils/roleUtils';
import TabNavigation from '../../components/TabNavigation';
import FileUploadWidget from '../../components/FileUploadWidget';
import SearchableSelect from '../../components/SearchableSelect';
import EligibilityBadge from '../../components/EligibilityBadge';
import { fetchAssignments, fetchBulkEligibility } from '../../services/contractorApi';
import AIPrefillPanel from '../../components/assistant/AIPrefillPanel';
import SimilarRecordsPanel from '../../components/assistant/SimilarRecordsPanel';
import AIDraftWarningBanner, { AISubmitReminder, AIVerifyAcknowledgement } from '../../components/assistant/AIDraftWarningBanner';
import useSimilarRecords from '../../hooks/useSimilarRecords';
import { applyDraft, draftFromSimilarRecord } from '../../utils/aiPrefill/ptwDraftMapper';
import { consumeDraft } from '../../utils/aiPrefill/handoff';
import { sendPrefillFeedback } from '../../services/assistantClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PTWCreate = ({ user, onLogout }) => {
  const navigate = useNavigate();
  // Emergency contact number rules follow the tenant country (REG-13).
  const contactCountry = defaultPhoneCountry();
  const contactRules = mobileRules(contactCountry);
  const { id: editId } = useParams();
  const location = useLocation();
  const isEditMode = location.pathname.includes('/edit/');
  const { showSuccess, showError, showWarning } = useAlert();
  const { tenant } = useAuth();
  const { activeSiteId, activeSiteName } = useSite();

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [permitNumber, setPermitNumber] = useState('');
  
  // Dates on this form are the TENANT's wall clock, not UTC and not the
  // browser's zone. The previous helper produced a UTC wall clock while the
  // submit below parsed it as browser-local, so on an IST browser a new permit
  // was stored as starting 5.5 hours in the past — and the field showed a time
  // nobody on site recognised.
  const tenantTimezone = tenant?.timezone || 'UTC';
  const getTodayDateTimeLocal = () => nowInTenantInput(tenantTimezone);

  /**
   * A permit's default validity window: it opens when the permit is raised and
   * runs for one shift.
   *
   * Twelve hours because that is the shift a permit is normally issued for, and
   * because the alternative — leaving Valid To empty — made every requester
   * type a date, and the validity gate then refuses the permit at issue if they
   * back-dated it. A sensible default they can shorten is better than a blank
   * they must fill.
   *
   * Both values are still fully editable, and the existing rule that Valid To
   * must be after Valid From continues to apply.
   */
  const PERMIT_DEFAULT_VALIDITY_HOURS = 12;

  // Twelve real hours later, expressed in the tenant's wall clock. Going via
  // the instant rather than adding to the string means a daylight-saving
  // transition inside the window still yields 12 hours of work, not 11 or 13.
  const getDefaultValidToLocal = (fromLocal) =>
    addHoursInTenantInput(fromLocal, PERMIT_DEFAULT_VALIDITY_HOURS, tenantTimezone);

  // Form data
  const [formData, setFormData] = useState({
    permit_type: '',
    permit_types: [],
    numbering_type: '',
    work_description: '',
    location: '',
    department: '',
    site_id: '',
    site_name: '',
    work_start_date: getTodayDateTimeLocal(),
    work_end_date: getDefaultValidToLocal(getTodayDateTimeLocal()),
    applicant_name: '',
    applicant_id: '',
    contractor_company: '',
    contractor_company_id: '',
    hazards_identified: [],
    control_measures: [],
    safety_checklist: [],
    workers: [],
    gas_test_readings: [],
    isolation_points: [],
    emergency_contact_name: '',
    emergency_contact_number: '',
    rescue_plan_available: false,
    rescue_plan_description: '',
    method_statement_url: '',
    risk_assessment_url: '',
    linked_hira_id: null,
    // Safe Work Permit format fields
    shift: '',
    ppe_required: ['Helmet', 'Safety Shoes'],  // baseline PPE, always required
    ppe_fire_extinguisher_no: '',
    ppe_locks_tags_no: '',
    ppe_other: '',
    esi_wc_covered: null,
    esi_wc_approved_by: '',
    tbt_attendees: [],
    jsa: [],
    housekeeping_done: null,
    contractor_supervisor_name: '',
    contractor_supervisor_mobile: '',
    safety_officer_name: '',
    safety_officer_mobile: '',
    authorizations: [],
    shift_handover: null
  });

  // Standard named authorization roles for the Safe Work Permit.
  const AUTH_ROLES = ['Permit Initiator', 'Dept. Lead', 'Area In-charge', 'EHS Dept.', 'Plant Head'];

  // PPE / safety-equipment options for the Safe Work Permit format.
  const PPE_OPTIONS = [
    'Safety Glasses', 'Hand Gloves', 'Face Shield / Welding Goggle', 'Apron',
    'Nose Mask / Respirator', 'Ear Plug / Muff', 'Helmet', 'Warning Signs',
    'Safety Harness / Lifeline', 'Scaffolds & Ladders', 'Forced Ventilation', 'Safety Shoes', 'Barricades'
  ];
  // Baseline PPE required for every permit type — always checked, cannot be removed.
  const MANDATORY_PPE = ['Helmet', 'Safety Shoes'];

  // Template data
  const [checklistTemplate, setChecklistTemplate] = useState([]);
  const [gasTestRequired, setGasTestRequired] = useState(false);
  const [isolationRequired, setIsolationRequired] = useState(false);
  const [rescuePlanRequired, setRescuePlanRequired] = useState(false);
  
  // Employee search
  const [employees, setEmployees] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [validatingCompetency, setValidatingCompetency] = useState(false);
  const [generatingJsa, setGeneratingJsa] = useState(false);

  // Registered contractor workers (external) — selectable as permit workers.
  const [contractorWorkers, setContractorWorkers] = useState([]);
  // Registered contractor companies — the Contractor Company dropdown source.
  const [contractorCompanies, setContractorCompanies] = useState([]);
  // Eligibility of the registered contractor workers on this permit, keyed by
  // contractor_worker_id. Fetched in ONE bulk call: a permit can carry a
  // hundred workers and a per-worker request would be a hundred round trips.
  const [workerEligibility, setWorkerEligibility] = useState({});
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  // Contractors actually engaged at this site for this work type, when site
  // assignments exist. Falls back to the full list so a tenant that has not
  // assigned anything yet is not left with an empty picker.
  const [siteContractorIds, setSiteContractorIds] = useState(null);
  const [addingCompany, setAddingCompany] = useState(false);   // inline "add new company" mode
  const [newCompanyName, setNewCompanyName] = useState('');
  const [savingCompany, setSavingCompany] = useState(false);
  
  // File attachments
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [initialAttachmentIds, setInitialAttachmentIds] = useState([]);

  // HIRA integration
  const [hiraRecords, setHiraRecords] = useState([]);
  const [selectedHIRA, setSelectedHIRA] = useState(null);

  // Department master (tenant-wide reference; dropdown source)
  const [departments, setDepartments] = useState([]);

  // Work shifts from company settings — Shift dropdown source.
  const [shifts, setShifts] = useState([]);

  // Shift-Change Hand-over is optional — off by default, revealed via a toggle.
  const [handoverEnabled, setHandoverEnabled] = useState(false);

  // Max length for the Work Description field (UI limit + live counter).
  const WORK_DESC_MAX = 1000;

  // AI prefill state (create mode only). aiMeta.applied tracks the values the
  // AI wrote; a field stays "unverified" until the user edits it (the diffing
  // effect below removes it from aiFields on any change).
  const [aiFields, setAiFields] = useState(new Set());
  const [aiMeta, setAiMeta] = useState(null); // { generationId, applied: {path: value}, certainty, appliedAt }
  // Mandatory pre-submit acknowledgement: once AI populated anything, the
  // permit cannot be submitted until this is ticked. Reset on every new draft.
  const [aiVerifiedAck, setAiVerifiedAck] = useState(false);
  // The form initializes work_start_date to "now" and work_end_date to
  // now + PERMIT_DEFAULT_VALIDITY_HOURS — for fill-empty-only purposes those
  // pristine defaults count as empty (the user never chose them), so an
  // AI-provided date may replace them. Any user-edited value wins.
  //
  // BOTH refs matter: with only the start date treated as pristine, "9am to
  // 5pm" applied the 9am start and silently kept the default end, producing a
  // validity window the user never asked for.
  const initialStartDateRef = useRef(null);
  const initialEndDateRef = useRef(null);
  if (initialStartDateRef.current === null) {
    initialStartDateRef.current = formData.work_start_date;
  }
  if (initialEndDateRef.current === null) {
    initialEndDateRef.current = formData.work_end_date;
  }

  // Similar past permits, driven by the work description (create mode only)
  const { suggestions: similarPermits, loading: similarLoading } = useSimilarRecords(
    'ptw', formData.work_description, { enabled: !isEditMode }
  );

  // Apply an AI draft (from the prefill panel, a similar record, or the chat
  // handoff) onto the form. Fill-empty-only; returns the apply report the
  // panel renders. ALWAYS raises the mandatory AI warning banner.
  // NOTE: the report is computed from the current snapshot and the merge is
  // re-run inside a functional update — React defers updater functions (seen
  // on the mount-effect path in React 19), so an updater side effect cannot
  // be read synchronously. applyDraft is pure, so running it twice is safe.
  const applyAIDraft = (result) => {
    const draft = result.draft || {};
    // Treat the untouched start AND end date defaults as empty (see refs above).
    const blankPristineDates = (fd) => {
      const out = { ...fd };
      if (out.work_start_date === initialStartDateRef.current) out.work_start_date = '';
      if (out.work_end_date === initialEndDateRef.current) out.work_end_date = '';
      return out;
    };
    // departments lets the mapper validate an AI-suggested department against
    // the tenant's master list, since the form's select stores the NAME.
    const ctx = { departments };
    const report = applyDraft(draft, blankPristineDates(formData), ctx);
    setFormData((prev) => applyDraft(draft, blankPristineDates(prev), ctx).formData);
    setAiVerifiedAck(false);
    const appliedValues = {};
    report.applied.forEach((f) => { appliedValues[f.path] = f.value; });
    setAiFields(new Set(report.applied.map((f) => f.path)));
    setAiMeta({
      generationId: result.generation_id || null,
      applied: appliedValues,
      appliedLabels: Object.fromEntries(report.applied.map((f) => [f.path, f.label])),
      certainty: result.certainty || {},
      appliedAt: Date.now(),
    });
    setCurrentStep(1);
    return report;
  };

  // A user edit to any AI-filled field marks it verified (drops the ring/count).
  useEffect(() => {
    if (!aiMeta || aiFields.size === 0) return;
    const stillAi = new Set();
    aiFields.forEach((path) => {
      if (JSON.stringify(formData[path]) === JSON.stringify(aiMeta.applied[path])) {
        stillAi.add(path);
      }
    });
    if (stillAi.size !== aiFields.size) setAiFields(stillAi);
  }, [formData]);

  const aiLowCertaintyFields = aiMeta
    ? [...aiFields]
        .filter((p) => aiMeta.certainty[p] === 'low')
        .map((p) => ({ path: p, label: aiMeta.appliedLabels?.[p] || p }))
    : [];

  // Subtle highlight for AI-filled, not-yet-verified inputs.
  const aiClass = (path) => (aiFields.has(path) ? ' ring-2 ring-amber-300 bg-amber-50' : '');

  // Chat → form handoff: consume a stashed draft on mount AND on every
  // navigation to this route (keyed on location.key — the widget's CTA can
  // fire while the user is already on /ptw/create, which pushes a new history
  // entry without remounting). consumeDraft is consume-on-read, so re-runs
  // and refreshes never re-apply a draft.
  useEffect(() => {
    if (isEditMode) return;
    const payload = consumeDraft('ptw');
    if (payload) {
      applyAIDraft({
        draft: payload.draft,
        generation_id: payload.generation_id,
        field_sources: payload.field_sources,
        certainty: payload.certainty,
        warnings: payload.warnings,
        rationale: payload.rationale,
      });
    }
    // eslint-disable-next-line
  }, [location.key]);

  const permitTypes = [
    'Hot Work',
    'Cold Work',
    'Confined Space',
    'Work at Height',
    'Electrical Work',
    'Excavation',
    'Lifting Operations',
    'Chemical Handling',
    'Isolation Permit',
    'Radiography',
    'General Maintenance'
  ];

  // Required certifications for each permit type
  const requiredCertifications = {
    'Hot Work': ['Hot Work Safety', 'Fire Prevention'],
    'Cold Work': [],
    'Confined Space': ['Confined Space Entry', 'Gas Testing', 'Rescue Operations'],
    'Work at Height': ['Work at Height', 'Fall Protection'],
    'Electrical Work': ['Electrical Safety', 'LOTO'],
    'Excavation': ['Excavation Safety', 'Shoring'],
    'Lifting Operations': ['Lifting Operations', 'Rigging', 'Crane Operations'],
    'Chemical Handling': ['Chemical Safety', 'Hazmat Handling'],
    'Isolation Permit': ['LOTO', 'Energy Isolation'],
    'Radiography': ['Radiation Safety', 'Radiography Operations'],
    'General Maintenance': []
  };

  // Auto-set site from active site context (global site switcher)
  useEffect(() => {
    if (isEditMode) return;
    if (activeSiteId) {
      setFormData(prev => ({ ...prev, site_id: activeSiteId, site_name: activeSiteName || '' }));
    } else {
      setFormData(prev => ({ ...prev, site_id: '', site_name: '' }));
    }
  }, [activeSiteId, activeSiteName, isEditMode]);

  useEffect(() => {
    fetchEmployees();
    fetchContractorWorkers();
    fetchContractorCompanies();
    fetchHIRARecords();
    fetchDepartments();
    fetchShifts();
    if (isEditMode && editId) {
      fetchPermitData();
    }
  }, [activeSiteId]);

  // Default the applicant to the logged-in user (create mode only). It's still
  // selectable from the employee list; edit mode keeps the saved applicant.
  useEffect(() => {
    if (isEditMode) return;
    try {
      const u = JSON.parse(localStorage.getItem('user'));
      if (u) {
        setFormData(prev => {
          // Default the Permit Initiator authorization to the logged-in user too.
          const auths = [...(prev.authorizations || [])];
          if (!auths.some(a => a.role === 'Permit Initiator')) {
            if (u.name) auths.push({ role: 'Permit Initiator', name: u.name });
          }
          return {
            ...prev,
            applicant_name: prev.applicant_name || u.name || '',
            applicant_id: prev.applicant_id || u.id || u.user_id || '',
            authorizations: auths,
          };
        });
      }
    } catch (e) { /* ignore malformed user */ }
  }, [isEditMode]);

  // Once the employee list loads, resolve the logged-in user to their employee
  // master record (by id) and use that canonical NAME for the defaulted Applicant
  // and Permit Initiator — reliable even when the stored login object has no
  // `name`. Create mode only; won't override a manually-changed initiator.
  useEffect(() => {
    if (isEditMode || employees.length === 0) return;
    let u;
    try { u = JSON.parse(localStorage.getItem('user')); } catch (e) { return; }
    if (!u) return;
    const me = employees.find(e => String(e.id) === String(u.id || u.user_id));
    if (!me) return;
    setFormData(prev => {
      const next = { ...prev };
      // Applicant: keep the display name in sync with the master record.
      if (String(prev.applicant_id) === String(me.id) && prev.applicant_name !== me.name) {
        next.applicant_name = me.name;
      }
      // Permit Initiator: set when empty or still the (possibly blank) login name.
      const auths = [...(prev.authorizations || [])];
      const i = auths.findIndex(a => a.role === 'Permit Initiator');
      const stale = i < 0 || !auths[i].name || auths[i].name === (u.name || '');
      if (i < 0) auths.push({ role: 'Permit Initiator', name: me.name });
      else if (stale && auths[i].name !== me.name) auths[i] = { ...auths[i], name: me.name };
      next.authorizations = auths;
      return next;
    });
  }, [employees, isEditMode]);

  // Resolve the selected contractor company's id from its saved name once the
  // company list loads (needed to scope the worker picker on an edited permit).
  useEffect(() => {
    if (!formData.contractor_company || formData.contractor_company_id) return;
    const match = contractorCompanies.find(
      c => (c.name || '').toLowerCase() === formData.contractor_company.toLowerCase()
    );
    if (match) setFormData(prev => ({ ...prev, contractor_company_id: match.id }));
  }, [contractorCompanies, formData.contractor_company, formData.contractor_company_id]);

  // Set selected HIRA when hiraRecords are loaded and we have a linked_hira_id
  useEffect(() => {
    console.log('PTW Edit - HIRA useEffect triggered:', {
      isEditMode,
      linked_hira_id: formData.linked_hira_id,
      hiraRecordsCount: hiraRecords.length,
      selectedHIRA: selectedHIRA?.id
    });
    if (isEditMode && formData.linked_hira_id && hiraRecords.length > 0 && !selectedHIRA) {
      // Use string comparison to handle potential type mismatches
      const linkedHira = hiraRecords.find(h => String(h.id) === String(formData.linked_hira_id));
      console.log('PTW Edit - Found linked HIRA:', linkedHira);
      if (linkedHira) {
        setSelectedHIRA(linkedHira);
      }
    }
  }, [hiraRecords, formData.linked_hira_id, isEditMode, selectedHIRA]);

  // Fetch existing permit data for edit mode
  const fetchPermitData = async () => {
    try {
      setInitialLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/permits/${editId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const permit = response.data;
      console.log('PTW Edit - Loaded permit data:', permit);
      console.log('PTW Edit - Workers from backend:', permit.workers);
      console.log('PTW Edit - linked_hira_id from backend:', permit.linked_hira_id);

      setPermitNumber(permit.permit_number || '');

      // Convert ISO dates to datetime-local format
      // Used only for the two validity fields. Rendered in the TENANT's zone so
      // reopening a permit shows the same times the requester chose, rather
      // than those times shifted into UTC.
      const formatDateForInput = (isoDate) =>
        (isoDate ? toTenantLocalInput(isoDate, tenantTimezone) : '');

      // Ensure workers have id field for React key and handle null/undefined
      const workersArray = Array.isArray(permit.workers) ? permit.workers : [];
      const workersWithIds = workersArray.map((worker, index) => ({
        ...worker,
        id: worker.id || `worker-${index}-${Math.random().toString(36).substr(2, 9)}`,
        // Certificates are stored without a client id; assign one for editing.
        certifications: Array.isArray(worker.certifications)
          ? worker.certifications.map((c, ci) => ({
              name: c.name || '',
              certificate_number: c.certificate_number || '',
              issuing_authority: c.issuing_authority || '',
              expiry_date: c.expiry_date || '',
              id: c.id || `cert-${index}-${ci}-${Math.random().toString(36).substr(2, 9)}`,
            }))
          : []
      }));
      console.log('PTW Edit - Processed workers:', workersWithIds);

      // Ensure linked_hira_id is a string for proper comparison
      const linkedHiraId = permit.linked_hira_id ? String(permit.linked_hira_id) : null;

      setFormData({
        permit_type: permit.permit_type || '',
        permit_types: (Array.isArray(permit.permit_types) && permit.permit_types.length > 0)
          ? permit.permit_types
          : (permit.permit_type ? [permit.permit_type] : []),
        // Number is already assigned on an existing permit; default the selector
        // to the primary type (numbering only takes effect when creating).
        numbering_type: permit.numbering_type || permit.permit_type || '',
        work_description: permit.work_description || '',
        location: permit.location || '',
        department: permit.department || '',
        site_id: permit.site_id || '',
        site_name: permit.site_name || '',
        work_start_date: formatDateForInput(permit.work_start_date),
        work_end_date: formatDateForInput(permit.work_end_date),
        applicant_name: permit.applicant_name || '',
        applicant_id: permit.applicant_id || '',
        contractor_company: permit.contractor_company || '',
        contractor_company_id: permit.contractor_company_id || '',
        hazards_identified: permit.hazards_identified || [],
        control_measures: permit.control_measures || [],
        safety_checklist: permit.safety_checklist || [],
        workers: workersWithIds,
        gas_test_readings: (permit.gas_test_readings || []).map((r, ri) => ({
          ...r,
          id: r.id || `gas-${ri}-${Math.random().toString(36).substr(2, 9)}`,
          // Custom fields are stored without a client id; assign one for editing.
          custom_fields: Array.isArray(r.custom_fields)
            ? r.custom_fields.map((f, fi) => ({ ...f, id: f.id || `gcf-${ri}-${fi}-${Math.random().toString(36).substr(2, 9)}` }))
            : []
        })),
        isolation_points: (permit.isolation_points || []).map((p, pi) => ({
          ...p,
          id: p.id || `iso-${pi}-${Math.random().toString(36).substr(2, 9)}`,
          custom_fields: Array.isArray(p.custom_fields)
            ? p.custom_fields.map((f, fi) => ({ ...f, id: f.id || `icf-${pi}-${fi}-${Math.random().toString(36).substr(2, 9)}` }))
            : []
        })),
        emergency_contact_name: permit.emergency_contact_name || '',
        emergency_contact_number: toNationalDigits(permit.emergency_contact_number || '', contactCountry),
        rescue_plan_available: permit.rescue_plan_available || false,
        rescue_plan_description: permit.rescue_plan_description || '',
        method_statement_url: permit.method_statement_url || '',
        risk_assessment_url: permit.risk_assessment_url || '',
        linked_hira_id: linkedHiraId,
        // Safe Work Permit format fields
        shift: permit.shift || '',
        ppe_required: Array.from(new Set([...MANDATORY_PPE, ...(Array.isArray(permit.ppe_required) ? permit.ppe_required : [])])),
        ppe_fire_extinguisher_no: permit.ppe_fire_extinguisher_no || '',
        ppe_locks_tags_no: permit.ppe_locks_tags_no || '',
        ppe_other: permit.ppe_other || '',
        esi_wc_covered: (permit.esi_wc_covered === true || permit.esi_wc_covered === false) ? permit.esi_wc_covered : null,
        esi_wc_approved_by: permit.esi_wc_approved_by || '',
        tbt_attendees: Array.isArray(permit.tbt_attendees)
          ? permit.tbt_attendees.map((a, i) => ({ ...a, id: a.id || `tbt-${i}-${Math.random().toString(36).substr(2, 9)}` }))
          : [],
        jsa: Array.isArray(permit.jsa)
          ? permit.jsa.map((j, i) => ({ ...j, id: j.id || `jsa-${i}-${Math.random().toString(36).substr(2, 9)}` }))
          : [],
        housekeeping_done: (permit.housekeeping_done === true || permit.housekeeping_done === false) ? permit.housekeeping_done : null,
        contractor_supervisor_name: permit.contractor_supervisor_name || '',
        contractor_supervisor_mobile: permit.contractor_supervisor_mobile || '',
        safety_officer_name: permit.safety_officer_name || '',
        safety_officer_mobile: permit.safety_officer_mobile || '',
        authorizations: Array.isArray(permit.authorizations) ? permit.authorizations : [],
        shift_handover: permit.shift_handover || null
      });

      // Reveal the hand-over section if the saved permit has any hand-over data.
      setHandoverEnabled(
        !!(permit.shift_handover &&
           Object.values(permit.shift_handover).some(v => (v || '').toString().trim() !== ''))
      );

      // Set template requirements based on permit type
      setGasTestRequired(permit.gas_test_required || false);
      setIsolationRequired(permit.isolation_required || false);
      setRescuePlanRequired(permit.permit_type === 'Confined Space');

      // Fetch existing attachments
      try {
        const attachRes = await axios.get(`${API}/permits/${editId}/attachments`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (attachRes.data?.attachments) {
          const files = attachRes.data.attachments.map(a => ({
            id: a.id,
            filename: a.filename,
            content_type: a.content_type,
            file_size: a.file_size,
            uploaded_by: a.uploaded_by,
          }));
          setUploadedFiles(files);
          setInitialAttachmentIds(files.map(f => f.id));
        }
      } catch (attachErr) {
        console.error('Failed to fetch attachments:', attachErr);
      }

      // Set selected HIRA if linked (may not work if hiraRecords not loaded yet)
      if (linkedHiraId && hiraRecords.length > 0) {
        const linkedHira = hiraRecords.find(h => String(h.id) === linkedHiraId);
        if (linkedHira) {
          setSelectedHIRA(linkedHira);
        }
      }

      setInitialLoading(false);
    } catch (err) {
      console.error('Failed to fetch permit:', err);
      showError('Failed to load permit data');
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    if (formData.permit_types && formData.permit_types.length > 0) {
      fetchPermitTemplates(formData.permit_types);
    } else {
      setChecklistTemplate([]);
      setGasTestRequired(false);
      setIsolationRequired(false);
      setRescuePlanRequired(false);
    }
  }, [(formData.permit_types || []).join('|')]);

  // Union of required certifications across all selected permit types.
  const getRequiredCerts = () => Array.from(new Set(
    (formData.permit_types || []).flatMap(t => requiredCertifications[t] || [])
  ));

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = activeSiteId ? `?site_id=${activeSiteId}` : '';
      const response = await axios.get(`${API}/users${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  };

  const fetchHIRARecords = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/risk-assessments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('PTW Edit - Loaded HIRA records:', response.data.length, 'records');
      console.log('PTW Edit - HIRA IDs:', response.data.map(h => h.id));
      setHiraRecords(response.data);
    } catch (err) {
      console.error('Failed to fetch HIRA records:', err);
    }
  };

  const fetchContractorWorkers = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = activeSiteId ? `?site_id=${activeSiteId}` : '';
      const response = await axios.get(`${API}/contractors/workers${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Only active workers are selectable.
      const list = (response.data.workers || []).filter(w => (w.status || 'active') === 'active');
      setContractorWorkers(list);
    } catch (err) {
      console.error('Failed to fetch contractor workers:', err);
      setContractorWorkers([]);
    }
  };

  // Stable keys for the effects below. Derived here rather than inline in a
  // dependency array so the arrays hold plain values — the same approach the
  // dashboard widgets use for their time/dimension keys.
  const selectedWorkerKey = JSON.stringify(
    (formData.workers || []).map(w => w.contractor_worker_id).filter(Boolean),
  );
  const permitWorkTypeKey = (formData.permit_types || [])[0]
    || formData.permit_type || '';

  // Re-evaluated when the worker set, the site or the permit type changes,
  // because all three are inputs to eligibility.
  useEffect(() => {
    const ids = (formData.workers || [])
      .map(w => w.contractor_worker_id)
      .filter(Boolean);
    if (!ids.length) {
      setWorkerEligibility({});
      return undefined;
    }
    const controller = new AbortController();
    setEligibilityLoading(true);
    fetchBulkEligibility({
      workerIds: ids,
      siteId: activeSiteId,
      workType: permitWorkTypeKey || undefined,
    }, { signal: controller.signal })
      .then(res => setWorkerEligibility(res.eligibility || {}))
      .catch(err => {
        if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
        // Advisory information: never block the form because it could not load.
        setWorkerEligibility({});
      })
      .finally(() => setEligibilityLoading(false));
    return () => controller.abort();
  }, [selectedWorkerKey, activeSiteId, permitWorkTypeKey, formData.workers]);

  // Which contractors are engaged at this site for this work type.
  useEffect(() => {
    if (!activeSiteId) { setSiteContractorIds(null); return undefined; }
    const controller = new AbortController();
    fetchAssignments({ siteId: activeSiteId, workType: permitWorkTypeKey || undefined },
                     { signal: controller.signal })
      .then(res => {
        const ids = (res.assignments || []).map(a => a.contractor_company_id);
        // No assignments at all means the tenant has not started using them —
        // narrowing to an empty list would break contractor selection, so leave
        // the picker unfiltered until there is something to filter on.
        setSiteContractorIds(ids.length ? ids : null);
      })
      .catch(() => setSiteContractorIds(null));
    return () => controller.abort();
  }, [activeSiteId, permitWorkTypeKey]);

  const fetchContractorCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/contractors/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = (response.data.companies || []).filter(c => (c.status || 'active') !== 'blacklisted');
      setContractorCompanies(list);
    } catch (err) {
      console.error('Failed to fetch contractor companies:', err);
      setContractorCompanies([]);
    }
  };

  // Select a contractor company and auto-populate the supervisor fields from the
  // company's registered contact (contact_person / contact_phone).
  const selectCompany = (c) => setFormData(prev => ({
    ...prev,
    contractor_company_id: c?.id || '',
    contractor_company: c?.name || '',
    contractor_supervisor_name: c?.contact_person || '',
    // REG-13: reduce a stored country code/formatting (e.g. "+91-98765…",
    // "+60 12-345 6789") to national digits for the tenant's phone country
    // (India for legacy tenants) instead of blindly keeping the last 10
    // digits. The field stays fully editable afterwards.
    contractor_supervisor_mobile: toNationalDigits(c?.contact_phone || '', defaultPhoneCountry()),
  }));

  // Add a new contractor company to the master from the PTW form. Requires
  // contractor write permission; if the user lacks it we still set the typed
  // name on the permit so their workflow isn't blocked.
  const handleAddCompany = async () => {
    const name = (newCompanyName || '').trim();
    if (!name) return;
    // Already exists? Just select it.
    const existing = contractorCompanies.find(c => (c.name || '').toLowerCase() === name.toLowerCase());
    if (existing) {
      selectCompany(existing);
      setAddingCompany(false); setNewCompanyName('');
      return;
    }
    setSavingCompany(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/contractors/companies`, { name }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchContractorCompanies();
      setFormData(prev => ({ ...prev, contractor_company: name, contractor_company_id: res.data?.id || '' }));
      setAddingCompany(false); setNewCompanyName('');
      showSuccess(`Company "${name}" added.`);
    } catch (err) {
      if (err?.response?.status === 403) {
        // No permission to manage the master — keep the name on the permit only.
        setFormData(prev => ({ ...prev, contractor_company: name, contractor_company_id: '' }));
        setAddingCompany(false); setNewCompanyName('');
        showWarning(`You don't have permission to add companies to the master. "${name}" is set on this permit only.`);
      } else {
        showError('Failed to add company. Please try again.');
      }
    } finally {
      setSavingCompany(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/departments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDepartments(response.data.departments || []);
    } catch (err) {
      console.error('Failed to fetch departments:', err);
      setDepartments([]);
    }
  };

  // Work shifts come from company settings (tenant config). Only shifts with a
  // name are offered in the dropdown.
  const fetchShifts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tenant-admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = (response.data?.shifts || []).filter(s => (s.name || '').trim());
      setShifts(list);
    } catch (err) {
      console.error('Failed to fetch shifts:', err);
      setShifts([]);
    }
  };

  // Combined permit: fetch the template for each selected type, union the
  // requirement flags, and build one checklist with each item tagged by type.
  const fetchPermitTemplates = async (types) => {
    try {
      const token = localStorage.getItem('token');
      const results = await Promise.all(types.map(t =>
        axios.get(`${API}/permits/templates/${encodeURIComponent(t)}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
          .then(r => ({ type: t, data: r.data }))
          .catch(() => ({ type: t, data: { checklist: [], gas_test_required: false, isolation_required: false, rescue_plan_required: false } }))
      ));

      setGasTestRequired(results.some(r => r.data.gas_test_required));
      setIsolationRequired(results.some(r => r.data.isolation_required));
      setRescuePlanRequired(results.some(r => r.data.rescue_plan_required));

      // Combined checklist, each item tagged with its source permit type.
      const templateItems = [];
      results.forEach(r => (r.data.checklist || []).forEach(item => {
        templateItems.push({ question: item.question, permit_type: r.type });
      }));
      setChecklistTemplate(templateItems);

      // Always ensure a checklist section exists for every selected type (both
      // create and edit), preserving any checked/remarks the user already set.
      // Custom checkpoints are kept as-is.
      setFormData(prev => {
        const prevList = prev.safety_checklist || [];
        const rebuilt = templateItems.map(item => {
          // Prefer an exact (question + type) match; fall back to a legacy item
          // (same question, no type tag) so pre-combined permits keep their state.
          const existing =
            prevList.find(i => i.question === item.question && (i.permit_type || null) === (item.permit_type || null)) ||
            prevList.find(i => i.question === item.question && !i.permit_type);
          // Preserve the tri-state answer (yes/no/na); keep `checked` synced to it
          // so No/NA answers survive the rebuild (edit mode) and stay highlighted.
          const prevStatus = existing?.status || (existing?.checked ? 'yes' : null);
          return {
            id: existing?.id || Math.random().toString(36).substr(2, 9),
            question: item.question,
            permit_type: item.permit_type,
            status: prevStatus || null,
            checked: prevStatus === 'yes',
            remarks: existing?.remarks || '',
            is_custom: false,
          };
        });
        const customs = prevList.filter(i => i.is_custom);
        return { ...prev, safety_checklist: [...rebuilt, ...customs] };
      });
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    }
  };

  const validateWorkerCompetency = async (employeeId, employeeName) => {
    setValidatingCompetency(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/permits/validate-worker-competency`,
        {
          employee_id: employeeId,
          employee_name: employeeName,
          required_certifications: getRequiredCerts()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setValidatingCompetency(false);
      return response.data;
    } catch (err) {
      setValidatingCompetency(false);
      console.error('Competency validation failed:', err);
      showError('Failed to validate worker competency');
      return null;
    }
  };

  const handleAddWorker = async () => {
    // If a system user is selected, add them (with competency validation).
    // Otherwise, if free text was typed, add it as a manual (external) worker.
    if (!selectedEmployee) {
      if (employeeSearch.trim()) {
        handleAddManualWorker(employeeSearch.trim());
      }
      return;
    }

    const employeeId = selectedEmployee.id || selectedEmployee.user_id;
    const employeeName = selectedEmployee.name || selectedEmployee.email || 'Unknown';

    // Prevent adding the same worker twice
    if (formData.workers.some(w => w.employee_id === employeeId)) {
      showWarning(`${employeeName} is already added as a worker.`);
      setSelectedEmployee(null);
      setEmployeeSearch('');
      return;
    }

    const competencyData = await validateWorkerCompetency(employeeId, employeeName);

    if (competencyData) {
      setFormData(prev => ({
        ...prev,
        workers: [...prev.workers, {
          id: Math.random().toString(36).substr(2, 9),
          employee_id: employeeId,
          employee_name: competencyData.employee_name || employeeName,
          is_manual: false,
          required_certifications: getRequiredCerts(),
          has_valid_certifications: competencyData.has_valid_certifications,
          missing_certifications: competencyData.missing_certifications || []
        }]
      }));

      setSelectedEmployee(null);
      setEmployeeSearch('');
    }
  };

  // Derive competency for an external worker from the certificate details
  // they hold vs. the certs required for the permit type. A required cert is
  // "covered" by a non-expired certificate with a matching name.
  const computeManualCompetency = (requiredCerts, certifications) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const validNames = (certifications || [])
      .filter(c => (c.name || '').trim() && (!c.expiry_date || new Date(c.expiry_date) >= today))
      .map(c => c.name.trim().toLowerCase());
    const req = requiredCerts || [];
    const missing = req.filter(rc => !validNames.includes(rc.toLowerCase()));
    return {
      has_valid_certifications: req.length === 0 ? true : missing.length === 0,
      missing_certifications: missing,
    };
  };

  // Add a worker typed by name who is not a system user (e.g. external
  // contractor personnel). Their competency is evidenced by manually-entered
  // certificate details (added on the worker card).
  const handleAddManualWorker = (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;

    // Prevent duplicates: match a manual worker by name, or a selected user by name.
    if (formData.workers.some(w => (w.employee_name || '').trim().toLowerCase() === trimmed.toLowerCase())) {
      showWarning(`${trimmed} is already added as a worker.`);
      setSelectedEmployee(null);
      setEmployeeSearch('');
      return;
    }

    const requiredCerts = getRequiredCerts();
    const competency = computeManualCompetency(requiredCerts, []);
    setFormData(prev => ({
      ...prev,
      workers: [...prev.workers, {
        id: Math.random().toString(36).substr(2, 9),
        employee_id: '',
        employee_name: trimmed,
        is_manual: true,
        required_certifications: requiredCerts,
        certifications: [],
        ...competency,
      }]
    }));

    setSelectedEmployee(null);
    setEmployeeSearch('');
  };

  // Add a worker picked from the contractor registry. They are external (no
  // system login), so competency is evidenced via certificate details on the
  // worker card, same as a manually-added worker.
  const handleAddContractorWorker = (cw) => {
    if (!cw) return;
    const name = (cw.name || '').trim();
    if (!name) return;

    // Prevent duplicates by contractor worker id or by name.
    if (formData.workers.some(w =>
      (w.contractor_worker_id && String(w.contractor_worker_id) === String(cw.id)) ||
      (w.employee_name || '').trim().toLowerCase() === name.toLowerCase()
    )) {
      showWarning(`${name} is already added as a worker.`);
      setSelectedEmployee(null);
      setEmployeeSearch('');
      return;
    }

    const requiredCerts = getRequiredCerts();
    // Auto-populate certificate details from the worker's registry record, so
    // competency is evidenced without re-entering them.
    const certs = (Array.isArray(cw.certifications) ? cw.certifications : [])
      .filter(c => (c.name || '').trim())
      .map(c => ({
        id: Math.random().toString(36).substr(2, 9),
        name: c.name || '',
        certificate_number: c.certificate_number || '',
        issuing_authority: c.issuing_authority || '',
        expiry_date: c.expiry_date || '',
      }));
    const competency = computeManualCompetency(requiredCerts, certs);
    setFormData(prev => ({
      ...prev,
      workers: [...prev.workers, {
        id: Math.random().toString(36).substr(2, 9),
        employee_id: '',
        employee_name: name,
        is_manual: true,
        contractor_worker_id: cw.id,
        contractor_company: cw.contractor_company || '',
        required_certifications: requiredCerts,
        certifications: certs,
        ...competency,
      }]
    }));
    if (certs.length > 0) {
      showSuccess(`${name} added with ${certs.length} certificate(s) from the worker registry.`);
    }

    setSelectedEmployee(null);
    setEmployeeSearch('');
  };

  const handleRemoveWorker = (workerId) => {
    setFormData(prev => ({
      ...prev,
      workers: prev.workers.filter(w => w.id !== workerId)
    }));
  };

  // Certificate entry for external (manual) workers. Any change recomputes the
  // worker's competency from the entered certs vs. the permit's required certs.
  const applyWorkerCertUpdate = (workerId, updater) => {
    setFormData(prev => ({
      ...prev,
      workers: prev.workers.map(w => {
        if (w.id !== workerId) return w;
        const certifications = updater(w.certifications || []);
        const competency = computeManualCompetency(w.required_certifications || [], certifications);
        return { ...w, certifications, ...competency };
      })
    }));
  };

  const handleAddWorkerCertification = (workerId) => {
    applyWorkerCertUpdate(workerId, certs => [
      ...certs,
      { id: Math.random().toString(36).substr(2, 9), name: '', certificate_number: '', issuing_authority: '', expiry_date: '' }
    ]);
  };

  const handleUpdateWorkerCertification = (workerId, certId, key, value) => {
    applyWorkerCertUpdate(workerId, certs => certs.map(c => c.id === certId ? { ...c, [key]: value } : c));
  };

  const handleRemoveWorkerCertification = (workerId, certId) => {
    applyWorkerCertUpdate(workerId, certs => certs.filter(c => c.id !== certId));
  };

  const handleAddGasReading = () => {
    setFormData(prev => ({
      ...prev,
      gas_test_readings: [...prev.gas_test_readings, {
        id: Math.random().toString(36).substr(2, 9),
        test_time: new Date().toISOString(),
        oxygen_level: '',
        lel_level: '',
        h2s_level: '',
        co_level: '',
        tester_name: '',
        remarks: '',
        custom_fields: []
      }]
    }));
  };

  const handleUpdateGasReading = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      gas_test_readings: prev.gas_test_readings.map(reading =>
        reading.id === id ? { ...reading, [field]: value } : reading
      )
    }));
  };

  // Custom (user-defined) additional readings within a gas test reading.
  const handleAddGasCustomField = (readingId) => {
    setFormData(prev => ({
      ...prev,
      gas_test_readings: prev.gas_test_readings.map(reading =>
        reading.id === readingId
          ? { ...reading, custom_fields: [...(reading.custom_fields || []), { id: Math.random().toString(36).substr(2, 9), label: '', value: '' }] }
          : reading
      )
    }));
  };

  const handleUpdateGasCustomField = (readingId, fieldId, key, value) => {
    setFormData(prev => ({
      ...prev,
      gas_test_readings: prev.gas_test_readings.map(reading =>
        reading.id === readingId
          ? { ...reading, custom_fields: (reading.custom_fields || []).map(f => f.id === fieldId ? { ...f, [key]: value } : f) }
          : reading
      )
    }));
  };

  const handleRemoveGasCustomField = (readingId, fieldId) => {
    setFormData(prev => ({
      ...prev,
      gas_test_readings: prev.gas_test_readings.map(reading =>
        reading.id === readingId
          ? { ...reading, custom_fields: (reading.custom_fields || []).filter(f => f.id !== fieldId) }
          : reading
      )
    }));
  };

  const handleAddIsolationPoint = () => {
    setFormData(prev => ({
      ...prev,
      isolation_points: [...prev.isolation_points, {
        id: Math.random().toString(36).substr(2, 9),
        equipment_tag: '',
        isolation_type: 'Electrical',
        lock_number: '',
        isolation_verified_by: '',
        remarks: '',
        custom_fields: []
      }]
    }));
  };

  const handleUpdateIsolationPoint = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      isolation_points: prev.isolation_points.map(point =>
        point.id === id ? { ...point, [field]: value } : point
      )
    }));
  };

  // Custom (user-defined) additional fields within an isolation/LOTO point.
  const handleAddIsolationCustomField = (pointId) => {
    setFormData(prev => ({
      ...prev,
      isolation_points: prev.isolation_points.map(point =>
        point.id === pointId
          ? { ...point, custom_fields: [...(point.custom_fields || []), { id: Math.random().toString(36).substr(2, 9), label: '', value: '' }] }
          : point
      )
    }));
  };

  const handleUpdateIsolationCustomField = (pointId, fieldId, key, value) => {
    setFormData(prev => ({
      ...prev,
      isolation_points: prev.isolation_points.map(point =>
        point.id === pointId
          ? { ...point, custom_fields: (point.custom_fields || []).map(f => f.id === fieldId ? { ...f, [key]: value } : f) }
          : point
      )
    }));
  };

  const handleRemoveIsolationCustomField = (pointId, fieldId) => {
    setFormData(prev => ({
      ...prev,
      isolation_points: prev.isolation_points.map(point =>
        point.id === pointId
          ? { ...point, custom_fields: (point.custom_fields || []).filter(f => f.id !== fieldId) }
          : point
      )
    }));
  };

  const handleChecklistChange = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      safety_checklist: prev.safety_checklist.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
    }));
  };

  // Tri-state (YES/NO/NA) answer for a checklist item; keeps `checked` in sync.
  const handleChecklistStatus = (id, status) => {
    setFormData(prev => ({
      ...prev,
      safety_checklist: prev.safety_checklist.map(item =>
        item.id === id ? { ...item, status, checked: status === 'yes' } : item
      )
    }));
  };

  // --- Safe Work Permit field handlers ---
  const togglePpe = (item) => {
    if (MANDATORY_PPE.includes(item)) return;  // baseline PPE can't be toggled off
    setFormData(prev => {
      const cur = prev.ppe_required || [];
      return { ...prev, ppe_required: cur.includes(item) ? cur.filter(p => p !== item) : [...cur, item] };
    });
  };
  const updateTbtAttendee = (id, key, value) => setFormData(prev => ({
    ...prev, tbt_attendees: (prev.tbt_attendees || []).map(a => a.id === id ? { ...a, [key]: value } : a)
  }));
  // A TBT attendee corresponds to a selected worker — match by worker_id (new
  // selections) or by name (attendees loaded from a saved permit).
  const isTbtWorkerSelected = (w) =>
    (formData.tbt_attendees || []).some(a => a.worker_id === w.id || a.name === w.employee_name);
  // Toggle a worker in/out of the Tool Box Talk attendee list (multiselect).
  const toggleTbtWorker = (w) => setFormData(prev => {
    const list = prev.tbt_attendees || [];
    if (list.some(a => a.worker_id === w.id || a.name === w.employee_name)) {
      return { ...prev, tbt_attendees: list.filter(a => !(a.worker_id === w.id || a.name === w.employee_name)) };
    }
    return {
      ...prev,
      tbt_attendees: [...list, {
        id: Math.random().toString(36).substr(2, 9),
        worker_id: w.id,
        name: w.employee_name,
        job_profile: '',
        signature: '',
      }],
    };
  });
  const addJsaRow = () => setFormData(prev => ({
    ...prev, jsa: [...(prev.jsa || []), { id: Math.random().toString(36).substr(2, 9), step: '', hazard: '', control: '' }]
  }));
  const updateJsaRow = (id, key, value) => setFormData(prev => ({
    ...prev, jsa: (prev.jsa || []).map(j => j.id === id ? { ...j, [key]: value } : j)
  }));
  const removeJsaRow = (id) => setFormData(prev => ({
    ...prev, jsa: (prev.jsa || []).filter(j => j.id !== id)
  }));
  // AI-draft the PPE selection + JSA from permit type(s) + work description
  // (grounded on any hazards/controls already pulled from a linked HIRA).
  // PPE is merged into the current selection; JSA rows are appended. Everything
  // stays fully editable — a draft for human review.
  const handleGenerateAI = async () => {
    if (!(formData.work_description || '').trim() && (formData.permit_types || []).length === 0) {
      showWarning('Add a work description and permit type before generating.');
      return;
    }
    setGeneratingJsa(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/permits/ai-assist`, {
        permit_types: formData.permit_types || [],
        work_description: formData.work_description || '',
        location: formData.location || '',
        hazards: formData.hazards_identified || [],
        controls: formData.control_measures || [],
        allowed_ppe: PPE_OPTIONS,
      }, { headers: { Authorization: `Bearer ${token}` } });

      const rows = (res.data?.jsa || []).map(r => ({
        id: Math.random().toString(36).substr(2, 9),
        step: r.step || '', hazard: r.hazard || '', control: r.control || ''
      }));
      const suggestedPpe = (res.data?.ppe || []).filter(p => PPE_OPTIONS.includes(p));

      if (rows.length === 0 && suggestedPpe.length === 0) {
        if (res.data?.ai_available === false) {
          showWarning('The AI service is not reachable in this environment. Please fill PPE and JSA manually.');
        } else {
          showWarning('AI could not draft suggestions for this permit. Please fill in manually.');
        }
        return;
      }

      setFormData(prev => ({
        ...prev,
        // Merge AI-suggested PPE into the existing selection (no duplicates).
        ppe_required: Array.from(new Set([...(prev.ppe_required || []), ...suggestedPpe])),
        jsa: [...(prev.jsa || []), ...rows],
      }));
      showSuccess(`AI added ${suggestedPpe.length} PPE item(s) and ${rows.length} JSA row(s). Review and edit before submitting.`);
    } catch (e) {
      showError('Failed to generate. Please try again or fill in manually.');
    } finally {
      setGeneratingJsa(false);
    }
  };
  // Upsert the person for a named authorization role.
  const setAuthName = (role, name) => setFormData(prev => {
    const list = [...(prev.authorizations || [])];
    const i = list.findIndex(a => a.role === role);
    if (i >= 0) list[i] = { ...list[i], name };
    else list.push({ role, name });
    return { ...prev, authorizations: list };
  });
  const getAuthName = (role) => ((formData.authorizations || []).find(a => a.role === role)?.name) || '';
  const setHandover = (key, value) => setFormData(prev => ({
    ...prev, shift_handover: { ...(prev.shift_handover || {}), [key]: value }
  }));

  // Options for an employee-name <select> (Authorization / Hand-over). Stores the
  // employee NAME. Keeps any current value that isn't in the list (e.g. a name
  // saved before this employee left) as a selectable option so it isn't dropped.
  const employeeNameOptions = (currentValue) => (
    <>
      <option value="">— Select employee —</option>
      {currentValue && !employees.some(e => e.name === currentValue) && (
        <option value={currentValue}>{currentValue}</option>
      )}
      {employees.map(e => (
        <option key={e.id} value={e.name}>{e.name}{e.role ? ` — ${formatRoleName(e.role)}` : ''}</option>
      ))}
    </>
  );


  const [customCheckpoint, setCustomCheckpoint] = useState('');

  const handleAddCheckpoint = () => {
    if (!customCheckpoint.trim()) return;
    const newItem = {
      id: `custom-${Date.now()}`,
      question: customCheckpoint.trim(),
      checked: false,
      remarks: '',
      is_custom: true
    };
    setFormData(prev => ({
      ...prev,
      safety_checklist: [...prev.safety_checklist, newItem]
    }));
    setCustomCheckpoint('');
  };

  const handleRemoveCheckpoint = (id) => {
    setFormData(prev => ({
      ...prev,
      safety_checklist: prev.safety_checklist.filter(item => item.id !== id)
    }));
  };

  const linkHIRA = async (hiraId) => {
    // Handle clearing the selection
    if (!hiraId) {
      setSelectedHIRA(null);
      setFormData(prev => ({
        ...prev,
        linked_hira_id: null,
        hazards_identified: [],
        control_measures: []
      }));
      return;
    }

    try {
      // Fetch full HIRA detail to get hazards and controls
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/risk-assessments/${hiraId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const hira = response.data;

      setSelectedHIRA(hira);

      // Extract hazard descriptions and control descriptions (deduplicated)
      const hazardSet = new Set();
      const controlSet = new Set();

      if (hira.hazards && Array.isArray(hira.hazards)) {
        hira.hazards.forEach(h => {
          const hazardText = h.hazard_description || h.title;
          if (hazardText) hazardSet.add(hazardText);
          if (h.existing_controls && Array.isArray(h.existing_controls)) {
            h.existing_controls.forEach(c => { if (c) controlSet.add(c); });
          }
          if (h.control_measures && Array.isArray(h.control_measures)) {
            h.control_measures.forEach(cm => {
              if (cm.description) controlSet.add(cm.description);
            });
          }
        });
      }

      const hazards = [...hazardSet];
      const controls = [...controlSet];

      setFormData(prev => ({
        ...prev,
        linked_hira_id: hiraId,
        hazards_identified: hazards,
        control_measures: controls
      }));
    } catch (err) {
      console.error('Failed to fetch HIRA details:', err);
      // Still link the HIRA even if detail fetch fails
      setFormData(prev => ({
        ...prev,
        linked_hira_id: hiraId
      }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.site_id) {
      showError('Please select a site from the header before submitting.');
      return;
    }
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user'));


      // Ensure applicant_id is available (user.id from login response)
      const userId = user.id || user.user_id;
      if (!user || !userId) {
        showError('User session invalid. Please log in again.');
        setLoading(false);
        return;
      }

      // Validate: Work End Date/Time must be after Work Start Date/Time
      if (formData.work_start_date && formData.work_end_date) {
        const startDate = new Date(formData.work_start_date);
        const endDate = new Date(formData.work_end_date);
        if (endDate <= startDate) {
          showError('Work Permit Valid To must be after Work Permit Valid From');
          setLoading(false);
          return;
        }
      }

      // Validate: Safety Checklist (tri-state) — each item must be answered
      // (YES/NO/NA, or legacy `checked`) or carry remarks; a NO needs remarks.
      // Mirrors the Next-step and backend validation.
      if (formData.safety_checklist && formData.safety_checklist.length > 0) {
        const incompleteItems = formData.safety_checklist.filter(item => {
          const st = item.status || (item.checked ? 'yes' : null);
          return (!st && !item.remarks) || (st === 'no' && !item.remarks);
        });
        if (incompleteItems.length > 0) {
          showError(`Safety Checklist incomplete: ${incompleteItems.length} item(s) are unanswered, or marked No without remarks. Please complete all checklist items.`);
          setLoading(false);
          return;
        }
      }

      // Validate: worker certificate expiry dates must not be in the past
      const _todayStr = new Date().toISOString().slice(0, 10);
      const hasPastCert = (formData.workers || []).some(w =>
        (w.certifications || []).some(c => (c.expiry_date || '') && c.expiry_date < _todayStr)
      );
      if (hasPastCert) {
        showError('A worker certificate has an expiry date in the past. Please correct it before submitting.');
        setLoading(false);
        return;
      }

      // Prepare payload - match backend PermitToWork model
      const payload = {
        // Carry the active site so multi-site users are associated correctly.
        // Validated above; backend rejects an empty site_id for multi-site users.
        site_id: formData.site_id,
        site_name: formData.site_name || '',
        permit_type: (formData.permit_types && formData.permit_types[0]) || formData.permit_type,
        permit_types: formData.permit_types || [],
        // Which selected type's numbering config drives the permit number.
        numbering_type: formData.numbering_type || (formData.permit_types && formData.permit_types[0]) || formData.permit_type || null,
        work_description: formData.work_description,
        location: formData.location,
        department: formData.department || null,
        applicant_id: formData.applicant_id || userId,
        applicant_name: formData.applicant_name || user.name || 'Unknown',
        contractor_company: formData.contractor_company || '',
        contractor_company_id: formData.contractor_company_id || null,
        // The field holds the TENANT's wall clock, so it is converted with the
        // tenant zone. `new Date(value)` would read it as the browser's zone
        // and shift the permit by the difference between the two.
        work_start_date: tenantInputToInstant(
          formData.work_start_date, tenantTimezone)?.toISOString(),
        work_end_date: tenantInputToInstant(
          formData.work_end_date, tenantTimezone)?.toISOString(),
        hazards_identified: formData.hazards_identified || [],
        control_measures: formData.control_measures || [],
        safety_checklist: formData.safety_checklist || [],
        workers: (formData.workers || []).map(w => ({
          ...w,
          // Keep only named certificates; strip the client-only id.
          certifications: (w.certifications || [])
            .filter(c => (c.name || '').trim() !== '')
            .map(c => ({
              name: c.name.trim(),
              certificate_number: (c.certificate_number || '').trim() || null,
              issuing_authority: (c.issuing_authority || '').trim() || null,
              expiry_date: (c.expiry_date || '').trim() || null,
            }))
        })),
        gas_test_readings: (formData.gas_test_readings || []).map(reading => {
          // Fields are optional — send blanks as null so the backend accepts
          // partially-filled readings instead of rejecting them.
          const blankToNull = (v) => (v === '' || v === null || v === undefined ? null : v);
          const t = reading.test_time ? new Date(reading.test_time) : null;
          return {
            ...reading,
            test_time: (t && !isNaN(t.getTime())) ? t.toISOString() : null,
            oxygen_level: blankToNull(reading.oxygen_level),
            lel_level: blankToNull(reading.lel_level),
            h2s_level: blankToNull(reading.h2s_level),
            co_level: blankToNull(reading.co_level),
            tester_name: blankToNull(reading.tester_name),
            remarks: blankToNull(reading.remarks),
            // Keep only labelled custom fields; strip the client-only id.
            custom_fields: (reading.custom_fields || [])
              .filter(f => (f.label || '').trim() !== '')
              .map(f => ({ label: f.label.trim(), value: (f.value ?? '').toString() }))
          };
        }),
        gas_test_required: gasTestRequired,
        isolation_points: (formData.isolation_points || []).map(point => ({
          ...point,
          // Keep only labelled custom fields; strip the client-only id.
          custom_fields: (point.custom_fields || [])
            .filter(f => (f.label || '').trim() !== '')
            .map(f => ({ label: f.label.trim(), value: (f.value ?? '').toString() }))
        })),
        isolation_required: isolationRequired,
        emergency_contact_name: formData.emergency_contact_name,
        emergency_contact_number: toContactSubmitValue(formData.emergency_contact_number, contactCountry),
        rescue_plan_available: formData.rescue_plan_available || false,
        rescue_plan_description: formData.rescue_plan_description || '',
        method_statement_url: formData.method_statement_url || '',
        risk_assessment_url: formData.risk_assessment_url || '',
        linked_hira_id: formData.linked_hira_id || null,
        // Safe Work Permit format fields
        shift: formData.shift || null,
        ppe_required: Array.from(new Set([...MANDATORY_PPE, ...(formData.ppe_required || [])])),
        ppe_fire_extinguisher_no: formData.ppe_fire_extinguisher_no || null,
        ppe_locks_tags_no: formData.ppe_locks_tags_no || null,
        ppe_other: formData.ppe_other || null,
        esi_wc_covered: formData.esi_wc_covered,
        esi_wc_approved_by: formData.esi_wc_approved_by || null,
        tbt_attendees: (formData.tbt_attendees || [])
          .filter(a => (a.name || '').trim() !== '')
          .map(a => ({ name: a.name.trim(), job_profile: (a.job_profile || '').trim() || null, signature: (a.signature || '').trim() || null })),
        jsa: (formData.jsa || [])
          .filter(j => (j.step || '').trim() !== '')
          .map(j => ({ step: j.step.trim(), hazard: (j.hazard || '').trim() || null, control: (j.control || '').trim() || null })),
        housekeeping_done: formData.housekeeping_done,
        contractor_supervisor_name: formData.contractor_supervisor_name || null,
        contractor_supervisor_mobile: formData.contractor_supervisor_mobile || null,
        safety_officer_name: formData.safety_officer_name || null,
        safety_officer_mobile: formData.safety_officer_mobile || null,
        authorizations: (formData.authorizations || [])
          .filter(a => (a.name || '').trim() !== '')
          .map(a => ({ role: a.role, name: a.name.trim(), authorized_at: a.authorized_at || null })),
        shift_handover: (formData.shift_handover && Object.values(formData.shift_handover).some(v => (v || '').toString().trim() !== ''))
          ? formData.shift_handover : null,
        all_workers_competent: formData.workers && formData.workers.length > 0 ? formData.workers.every(w => w.has_valid_certifications) : true,
        file_ids: uploadedFiles.map(f => f.id)
      };

      // Only add permit_number and status for create mode
      if (!isEditMode) {
        payload.permit_number = ''; // Will be auto-generated by backend
        payload.status = 'Draft';
      }


      let response;
      if (isEditMode) {
        // Update existing permit (remove file_ids from PUT payload - handled separately)
        const { file_ids, ...updatePayload } = payload;
        response = await axios.put(`${API}/permits/${editId}`, updatePayload, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Sync attachments: detach removed files, attach new files
        const currentFileIds = uploadedFiles.map(f => f.id);
        const removedIds = initialAttachmentIds.filter(id => !currentFileIds.includes(id));
        const newIds = currentFileIds.filter(id => !initialAttachmentIds.includes(id));

        for (const fileId of removedIds) {
          try {
            await axios.delete(`${API}/permits/${editId}/attachments/${fileId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } catch (detachErr) {
            console.error('Failed to detach file:', detachErr);
          }
        }
        if (newIds.length > 0) {
          try {
            await axios.post(`${API}/permits/${editId}/attachments`, { file_ids: newIds }, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } catch (attachErr) {
            console.error('Failed to attach files:', attachErr);
          }
        }
      } else {
        // Create new permit (file_ids included in payload)
        response = await axios.post(`${API}/permits`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      showSuccess(isEditMode ? 'Permit updated successfully!' : 'Permit created successfully!');

      // Evaluation beacon: which AI-filled fields survived to submit
      // (field NAMES only, never values; failures are swallowed).
      if (!isEditMode && aiMeta?.generationId) {
        const appliedPaths = Object.keys(aiMeta.applied);
        const kept = [];
        const edited = [];
        const cleared = [];
        appliedPaths.forEach((path) => {
          const current = formData[path];
          if (JSON.stringify(current) === JSON.stringify(aiMeta.applied[path])) kept.push(path);
          else if (current === '' || current == null || (Array.isArray(current) && current.length === 0)) cleared.push(path);
          else edited.push(path);
        });
        sendPrefillFeedback({
          generation_id: aiMeta.generationId,
          module: 'ptw',
          fields_applied: appliedPaths,
          fields_kept: kept,
          fields_edited: edited,
          fields_cleared: cleared,
          submitted: true,
        });
      }

      setTimeout(() => {
        if (isEditMode) {
          navigate(`/ptw/${editId}`);
        } else {
          navigate('/ptw');
        }
      }, 1500);
    } catch (err) {
      console.error('Submit error:', err);
      let errorMsg = isEditMode ? 'Failed to update permit' : 'Failed to create permit';

      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          // Pydantic validation errors
          errorMsg = detail.map(e => `${e.loc.join('.')}: ${e.msg}`).join(', ');
        } else if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (typeof detail === 'object') {
          errorMsg = JSON.stringify(detail);
        }
      } else if (err.message) {
        errorMsg = err.message;
      }

      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { number: 1, title: 'Job Details & Workers', icon: '📋' },
    { number: 2, title: 'Hazards & Controls', icon: '⚠️' },
    { number: 3, title: 'Safety Checklist', icon: '✅' },
    { number: 4, title: 'Special Requirements', icon: '🛡️' }
  ];

  const filteredEmployees = employees.filter(emp => {
    const name = emp.name || '';
    const email = emp.email || '';
    const searchLower = employeeSearch.toLowerCase();
    return name.toLowerCase().includes(searchLower) || email.toLowerCase().includes(searchLower);
  });

  // Contractors offered in the picker: narrowed to those engaged at this site
  // for this work type when site assignments exist, so a plant sees the handful
  // relevant to the permit rather than every contractor in the tenant.
  const selectableContractorCompanies = siteContractorIds
    ? contractorCompanies.filter(c => siteContractorIds.includes(c.id))
    : contractorCompanies;

  // Sentinel for a company name that sits on the permit but is not in the
  // master — an older permit, or one saved by someone without permission to add
  // to the master. It must stay VISIBLE and selected, or opening such a permit
  // would look like no contractor was ever set.
  const UNLISTED = '__unlisted__';

  const contractorOptions = selectableContractorCompanies.map(c => ({
    value: String(c.id),
    label: c.name,
    // Registration number is the other thing a firm gets searched by.
    hint: c.registration_no || undefined,
    badge: (c.status || 'active').toLowerCase() === 'active' ? undefined : c.status,
  }));

  // A contractor already on the permit but not engaged at this site must stay
  // selectable, or editing the permit would silently clear its contractor.
  if (formData.contractor_company_id
      && !selectableContractorCompanies.some(
        c => String(c.id) === String(formData.contractor_company_id))) {
    const saved = contractorCompanies.find(
      x => String(x.id) === String(formData.contractor_company_id));
    if (saved) {
      contractorOptions.push({
        value: String(saved.id),
        label: saved.name,
        hint: 'Not engaged at this site',
      });
    }
  }

  if (formData.contractor_company && !formData.contractor_company_id) {
    contractorOptions.push({
      value: UNLISTED,
      label: formData.contractor_company,
      hint: 'On this permit only — not in the contractor master',
    });
  }

  const contractorSelectValue = formData.contractor_company_id
    ? String(formData.contractor_company_id)
    : (formData.contractor_company ? UNLISTED : '');

  const handleContractorSelect = (next) => {
    // The unlisted row IS the current state; re-picking it changes nothing.
    if (next === UNLISTED) return;
    // '' clears — selectCompany(undefined) empties the name, the id and the
    // supervisor fields it had auto-filled.
    selectCompany(contractorCompanies.find(x => String(x.id) === next));
  };

  const filteredContractorWorkers = contractorWorkers.filter(cw => {
    // Scope to the selected contractor company (by id, or by name for legacy
    // workers without a company id). No company selected → show all.
    if (formData.contractor_company_id) {
      const byId = cw.contractor_company_id && String(cw.contractor_company_id) === String(formData.contractor_company_id);
      const byName = (cw.contractor_company || '').toLowerCase() === (formData.contractor_company || '').toLowerCase();
      if (!byId && !byName) return false;
    }
    const q = employeeSearch.toLowerCase();
    if (!q) return true;
    return (
      (cw.name || '').toLowerCase().includes(q) ||
      (cw.contractor_company || '').toLowerCase().includes(q) ||
      (cw.trade || '').toLowerCase().includes(q) ||
      (cw.badge_no || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => isEditMode ? navigate(`/ptw/${editId}`) : navigate('/ptw')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {isEditMode ? 'Back to Permit' : 'Back to Permits'}
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditMode ? `Edit Permit ${permitNumber}` : 'Create Permit to Work'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isEditMode ? 'Update permit details' : 'Issue a new work permit for high-risk activities'}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold ${
                    currentStep >= step.number
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                    {currentStep > step.number ? <CheckCircle2 className="w-6 h-6" /> : step.icon}
                  </div>
                  <span className={`text-xs mt-2 font-semibold ${
                    currentStep >= step.number ? 'text-blue-600' : 'text-gray-500'
                  }`}>
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-4 ${
                    currentStep > step.number ? 'bg-blue-600' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Initial Loading for Edit Mode */}
        {initialLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-600">Loading permit data...</span>
          </div>
        )}

        {/* MANDATORY AI warning — shown every time data is pre-populated,
            sticky across all wizard steps, never dismissible while
            AI-filled fields remain unverified. */}
        {!isEditMode && (
          <div className="mb-4">
            <AIDraftWarningBanner
              aiFieldCount={aiFields.size}
              lowCertaintyFields={aiLowCertaintyFields}
              appliedAt={aiMeta?.appliedAt}
            />
          </div>
        )}

        {/* AI prefill (create mode only; renders null without ptw:create or
            when the safety_assistant module is disabled for the tenant) */}
        {!isEditMode && (
          <AIPrefillPanel
            module="ptw"
            onDraft={applyAIDraft}
            disabled={!activeSiteId}
            disabledHint="Select a site from the header first"
            placeholder="Describe the work, e.g. 'Welding repair on tank T-102 near the paint shop, 2 contractors from Apex, tomorrow 8am–5pm'"
          />
        )}

        {/* Form Card */}
        {!initialLoading && (
        <Card>
          <CardContent className="p-6">
            {/* Step 1: Job Details & Workers */}
            {currentStep === 1 && (
              <div className="space-y-6">
                {/* Site (auto-set from header site switcher) */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Site <span className="text-red-500">*</span>
                  </label>
                  {activeSiteId ? (
                    <div className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700">
                      {activeSiteName || 'Loading...'}
                    </div>
                  ) : (
                    <div className="w-full px-4 py-3 bg-amber-50 border border-amber-300 rounded-lg text-amber-800 text-sm">
                      Please select a site from the header first.
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Permit Type(s) <span className="text-red-500">*</span>
                    <span className="ml-2 text-xs font-normal text-gray-500">Select one or more — a combined permit covers all selected types</span>
                  </label>
                  {/* The single-select dropdown that used to live here was
                      superseded by this checkbox list (a permit may combine
                      several types). It was left behind writing only the legacy
                      `permit_type`, so choosing from it ticked no box, did not
                      satisfy the step gate and did not load the checklist
                      template — two controls for one field, one of which did
                      not work. `permit_type` is still derived from
                      `permit_types[0]` below for the submit payload. */}
                  <div className={`grid grid-cols-2 md:grid-cols-3 gap-2${aiClass('permit_types') ? ' rounded-lg p-2 ring-2 ring-amber-300 bg-amber-50' : ''}`}>
                    {permitTypes.map(type => {
                      const checked = (formData.permit_types || []).includes(type);
                      return (
                        <label key={type} className={`flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer text-sm ${checked ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:bg-gray-50'}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setFormData(prev => {
                                const cur = prev.permit_types || [];
                                const next = cur.includes(type) ? cur.filter(t => t !== type) : [...cur, type];
                                // Keep the numbering type valid: default to / fall back
                                // to the first selected type when the chosen one is gone.
                                const numbering_type = next.includes(prev.numbering_type)
                                  ? prev.numbering_type
                                  : (next[0] || '');
                                return { ...prev, permit_types: next, permit_type: next[0] || '', numbering_type };
                              });
                            }}
                            className="w-4 h-4"
                          />
                          <span className="text-gray-800">{type}</span>
                        </label>
                      );
                    })}
                  </div>

                  {/* When a combined permit spans multiple types (each with its own
                      numbering config), let the user choose which type's number is used. */}
                  {(formData.permit_types || []).length > 1 && (
                    <div className="mt-3">
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Permit number based on
                        <span className="ml-2 font-normal text-gray-500">
                          Each type may use a different numbering format — pick which one this permit's number follows
                        </span>
                      </label>
                      <select
                        value={formData.numbering_type || formData.permit_types[0]}
                        onChange={(e) => setFormData({ ...formData, numbering_type: e.target.value })}
                        className="w-full md:w-1/2 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      >
                        {(formData.permit_types || []).map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Work Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.work_description}
                    // onChange={(e) => setFormData({ ...formData, work_description: e.target.value })}
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none${aiClass('work_description')}`}
                    onChange={(e) => setFormData({ ...formData, work_description: e.target.value.slice(0, WORK_DESC_MAX) })}
                    // className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    rows="3"
                    placeholder="Describe the work to be performed"
                    maxLength={WORK_DESC_MAX}
                    required
                  />
                  {!isEditMode && (
                    <SimilarRecordsPanel
                      suggestions={similarPermits}
                      loading={similarLoading}
                      title="similar past permits"
                      onStartFrom={(rec) => applyAIDraft({ draft: draftFromSimilarRecord(rec) })}
                    />
                  )}
                  <div className="flex justify-between text-xs mt-1">
                    <span className="text-gray-500">
                      {formData.work_description.length} of {WORK_DESC_MAX} characters used
                    </span>
                    <span className={`${WORK_DESC_MAX - formData.work_description.length <= 50 ? 'text-amber-600' : 'text-gray-500'}`}>
                      {WORK_DESC_MAX - formData.work_description.length} remaining
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Location <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none${aiClass('location')}`}
                      placeholder="Work site location"
                      maxLength={255}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Department
                    </label>
                    <select
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      <option value="">— Select department —</option>
                      {/* Preserve a previously-set department even if it is no longer in the master */}
                      {formData.department && !departments.some(d => d.name === formData.department) && (
                        <option value={formData.department}>{formData.department}</option>
                      )}
                      {departments.map(d => (
                        <option key={d.id} value={d.name}>{d.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Applicant Name <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.applicant_id || ''}
                      onChange={(e) => {
                        const emp = employees.find(x => String(x.id) === e.target.value);
                        setFormData(prev => ({
                          ...prev,
                          applicant_id: e.target.value,
                          applicant_name: emp ? emp.name : prev.applicant_name,
                        }));
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      required
                    >
                      <option value="">— Select applicant —</option>
                      {/* Keep the current applicant selectable even if not in the site's employee list. */}
                      {formData.applicant_id && !employees.some(x => String(x.id) === String(formData.applicant_id)) && (
                        <option value={formData.applicant_id}>{formData.applicant_name || 'Current user'}</option>
                      )}
                      {employees.map(e => (
                        <option key={e.id} value={e.id}>{e.name}{e.role ? ` — ${formatRoleName(e.role)}` : ''}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Work Permit Valid From <span className="text-red-500">*</span>
                    </label>
                    <DateTimePicker
                      value={formData.work_start_date}
                      onChange={(rawValue) => setFormData((prev) => {
                        // The picker emits a full ISO instant, not a wall
                        // clock. Normalise on the way in so form state holds
                        // one representation throughout.
                        const value = toWallClockInput(rawValue);
                        return {
                        ...prev,
                        work_start_date: value,
                        // Carry the 12-hour window along, but ONLY while the
                        // requester has not chosen an end of their own —
                        // recognised by the end still matching the default
                        // derived from the previous start. Without this,
                        // moving the start to tomorrow leaves Valid To behind
                        // it and the form is invalid the moment it is touched.
                        work_end_date:
                          prev.work_end_date === getDefaultValidToLocal(prev.work_start_date)
                            ? getDefaultValidToLocal(value)
                            : prev.work_end_date,
                        };
                      })}
                      placeholder="Select start date and time"
                      maxDate={formData.work_end_date}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Work Permit Valid To <span className="text-red-500">*</span>
                    </label>
                    <DateTimePicker
                      value={formData.work_end_date}
                      onChange={(rawValue) => setFormData({
                        ...formData, work_end_date: toWallClockInput(rawValue),
                      })}
                      placeholder="Select end date and time"
                      minDate={formData.work_start_date}
                    />
                    {formData.work_start_date && formData.work_end_date && new Date(formData.work_end_date) <= new Date(formData.work_start_date) && (
                      <p className="text-red-500 text-sm mt-1">Valid To must be after Valid From</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Contractor Company
                    </label>
                    {!addingCompany ? (
                      <SearchableSelect
                        id="ptw-contractor"
                        value={contractorSelectValue}
                        options={contractorOptions}
                        onChange={handleContractorSelect}
                        placeholder="— None / Not applicable —"
                        searchPlaceholder="Search by name or registration…"
                        noResultsText="No contractor matches that search"
                        /* A footer, not an option: "add new" is needed exactly
                           when the search matches nothing, which is precisely
                           when a filtered-out option would have vanished. The
                           typed query pre-fills the name. */
                        renderFooter={({ query, close }) => (
                          <button
                            type="button"
                            onClick={() => {
                              setNewCompanyName(query || '');
                              setAddingCompany(true);
                              close();
                            }}
                            className="w-full px-4 py-2 text-left text-sm font-medium
                                       text-blue-600 hover:bg-blue-50"
                          >
                            ➕ Add {query ? `"${query}"` : 'new company'} to the
                            contractor master…
                          </button>
                        )}
                      />
                    ) : (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newCompanyName}
                          onChange={(e) => setNewCompanyName(e.target.value)}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          placeholder="New company name"
                          autoFocus
                        />
                        <Button type="button" size="sm" onClick={handleAddCompany} disabled={savingCompany || !newCompanyName.trim()}>
                          {savingCompany ? 'Adding…' : 'Add'}
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={() => { setAddingCompany(false); setNewCompanyName(''); }}>
                          Cancel
                        </Button>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Select a registered company, or add a new one. Contractor workers are then filtered to that company.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Shift
                    </label>
                    <select
                      value={formData.shift}
                      onChange={(e) => setFormData({ ...formData, shift: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      <option value="">Select shift</option>
                      {(() => {
                        // Store the full "Name (from–to)" label as the value so the
                        // detail view and PDF show the shift with its times without
                        // any lookup. Captures the times as of permit creation.
                        const shiftLabel = (s) => {
                          const times = [s.start_time, s.end_time].filter(Boolean).join('–');
                          return times ? `${s.name} (${times})` : s.name;
                        };
                        const labels = shifts.map(shiftLabel);
                        return (
                          <>
                            {/* Preserve a saved shift no longer in company settings. */}
                            {formData.shift && !labels.includes(formData.shift) && (
                              <option value={formData.shift}>{formData.shift}</option>
                            )}
                            {shifts.map((s) => {
                              const label = shiftLabel(s);
                              return <option key={s.name} value={label}>{label}</option>;
                            })}
                          </>
                        );
                      })()}
                    </select>
                    {shifts.length === 0 && (
                      <p className="text-xs text-gray-500 mt-1">No shifts configured. An admin can add them in Company Settings.</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Contractor Supervisor</label>
                    <input type="text" value={formData.contractor_supervisor_name} onChange={(e) => setFormData({ ...formData, contractor_supervisor_name: stripDigits(e.target.value) })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none" placeholder="Name" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Supervisor Mobile</label>
                    {/* REG-13: tenant-country digit rules (India 10 for legacy tenants) */}
                    <PhoneInput
                      value={formData.contractor_supervisor_mobile}
                      onChange={(v) => setFormData((prev) => ({ ...prev, contractor_supervisor_mobile: v }))}
                      defaultCountry={defaultPhoneCountry()}
                      showCountrySelect={false}
                      placeholder="Mobile number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Site Supervisor / Safety Officer</label>
                    <select
                      value={formData.safety_officer_name || ''}
                      onChange={(e) => {
                        const name = e.target.value;
                        const emp = employees.find(x => x.name === name);
                        setFormData(prev => ({
                          ...prev,
                          safety_officer_name: name,
                          // Auto-fill the mobile from the employee's phone when available
                          // (REG-13: national digits for the tenant's phone country).
                          safety_officer_mobile: (emp && emp.phone)
                            ? toNationalDigits(emp.phone, defaultPhoneCountry())
                            : prev.safety_officer_mobile,
                        }));
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      {employeeNameOptions(formData.safety_officer_name || '')}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Safety Officer Mobile</label>
                    <PhoneInput
                      value={formData.safety_officer_mobile}
                      onChange={(v) => setFormData((prev) => ({ ...prev, safety_officer_mobile: v }))}
                      defaultCountry={defaultPhoneCountry()}
                      showCountrySelect={false}
                      placeholder="Mobile number"
                    />
                  </div>
                </div>

                {/* Worker Competency Validation */}
                {formData.permit_types && formData.permit_types.length > 0 && (
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">
                      Workers & Competency Validation
                    </h3>

                    {getRequiredCerts().length > 0 && (
                      <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                        <p className="text-sm font-semibold text-blue-900 mb-2">Required Certifications:</p>
                        <div className="flex flex-wrap gap-2">
                          {getRequiredCerts().map(cert => (
                            <span key={cert} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                              {cert}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Add Worker
                      </label>
                      <div className="flex gap-2">
                        <div className="flex-1 relative">
                          <input
                            type="text"
                            value={employeeSearch}
                            onChange={(e) => {
                              setEmployeeSearch(e.target.value);
                              // Editing the text drops a stale system-user selection so
                              // the entry can be added as a manual worker instead.
                              if (selectedEmployee && e.target.value !== (selectedEmployee.name || '')) {
                                setSelectedEmployee(null);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddWorker();
                              }
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            placeholder="Search a system user or contractor worker, or type a name to add manually"
                          />
                          {employeeSearch.trim() && !selectedEmployee && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                              {/* System users */}
                              {filteredEmployees.length > 0 && (
                                <div className="px-3 py-1 text-xs font-semibold text-gray-500 bg-gray-50">System Users</div>
                              )}
                              {filteredEmployees.map(emp => (
                                <div
                                  key={emp.id}
                                  onClick={() => {
                                    setSelectedEmployee(emp);
                                    setEmployeeSearch(emp.name);
                                  }}
                                  className="px-4 py-2 hover:bg-blue-50 cursor-pointer"
                                >
                                  <div className="font-semibold text-gray-900">{emp.name}</div>
                                  <div className="text-xs text-gray-600">{emp.email}</div>
                                </div>
                              ))}

                              {/* Contractor workers (external, from the contractor registry) */}
                              {filteredContractorWorkers.length > 0 && (
                                <div className="px-3 py-1 text-xs font-semibold text-gray-500 bg-gray-50 border-t border-gray-100">Contractor Workers</div>
                              )}
                              {filteredContractorWorkers.map(cw => (
                                <div
                                  key={cw.id}
                                  onClick={() => handleAddContractorWorker(cw)}
                                  className="px-4 py-2 hover:bg-amber-50 cursor-pointer flex items-center justify-between"
                                >
                                  <div>
                                    <div className="font-semibold text-gray-900">{cw.name}</div>
                                    <div className="text-xs text-gray-600">
                                      {[cw.contractor_company, cw.trade, cw.badge_no && `Badge ${cw.badge_no}`].filter(Boolean).join(' · ') || 'Contractor worker'}
                                    </div>
                                  </div>
                                  <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold shrink-0">Contractor</span>
                                </div>
                              ))}

                              {/* Always offer adding the typed text as a manual (external) worker */}
                              <div
                                onClick={() => handleAddManualWorker(employeeSearch)}
                                className="px-4 py-2 border-t border-gray-100 hover:bg-amber-50 cursor-pointer flex items-center gap-2"
                              >
                                <Plus className="w-4 h-4 text-amber-600" />
                                <span className="text-sm text-gray-800">
                                  Add "<span className="font-semibold">{employeeSearch.trim()}</span>" as a worker
                                  <span className="text-gray-500"> (not a system user)</span>
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                        <Button
                          onClick={handleAddWorker}
                          disabled={(!selectedEmployee && !employeeSearch.trim()) || validatingCompetency}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          {validatingCompetency ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Plus className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Select a system user for automatic competency checks, or type a name to add an external worker (competency not verified in system).
                      </p>
                    </div>

                    {/* Workers List */}
                    {isEditMode && (
                      <div className="mb-2 text-xs text-gray-500">
                        Debug: {formData.workers.length} workers loaded
                      </div>
                    )}
                    {formData.workers.length > 0 && (
                      <div className="space-y-2">
                        {formData.workers.map(worker => (
                          <div
                            key={worker.id}
                            className={`p-4 rounded-lg border-2 ${
                              worker.has_valid_certifications
                                ? 'bg-green-50 border-green-200'
                                : worker.is_manual
                                ? 'bg-amber-50 border-amber-200'
                                : 'bg-red-50 border-red-200'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-gray-900">{worker.employee_name}</span>
                                  {worker.has_valid_certifications ? (
                                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                                  ) : (
                                    <AlertTriangle className={`w-5 h-5 ${worker.is_manual ? 'text-amber-600' : 'text-red-600'}`} />
                                  )}
                                  {worker.is_manual && (
                                    <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold">
                                      {worker.contractor_worker_id ? 'Contractor' : 'External'}
                                    </span>
                                  )}
                                </div>
                                {worker.contractor_company && (
                                  <p className="text-xs text-gray-600 mt-1">{worker.contractor_company}</p>
                                )}
                                {/* Work eligibility from the contractor master:
                                    induction, medical, gate pass, contractor
                                    documents and site assignment. Shown here so
                                    a blocker is visible BEFORE submit rather
                                    than surprising the issuer at the gate. */}
                                {worker.contractor_worker_id && (
                                  <div className="mt-1.5">
                                    <EligibilityBadge
                                      result={workerEligibility[worker.contractor_worker_id]}
                                      loading={eligibilityLoading
                                        && !workerEligibility[worker.contractor_worker_id]}
                                      compact
                                    />
                                  </div>
                                )}
                                {worker.is_manual && worker.has_valid_certifications && (worker.certifications || []).some(c => (c.name || '').trim()) && (
                                  <p className="text-xs text-green-700 mt-1">
                                    Competency verified from provided certificate(s).
                                  </p>
                                )}
                                {worker.is_manual && worker.has_valid_certifications && !(worker.certifications || []).some(c => (c.name || '').trim()) && (
                                  <p className="text-xs text-gray-600 mt-1">
                                    No certifications required for this permit type.
                                  </p>
                                )}
                                {!worker.has_valid_certifications && worker.missing_certifications && worker.missing_certifications.length > 0 && (
                                  <div className="mt-2">
                                    <p className={`text-sm font-semibold ${worker.is_manual ? 'text-amber-700' : 'text-red-700'}`}>
                                      {worker.is_manual ? 'Certificates required for:' : 'Missing Certifications:'}
                                    </p>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {worker.missing_certifications.map(cert => (
                                        <span key={cert} className={`px-2 py-1 rounded text-xs ${worker.is_manual ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>
                                          {cert}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={() => handleRemoveWorker(worker.id)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <X className="w-5 h-5" />
                              </button>
                            </div>

                            {/* Certificate details for external workers */}
                            {worker.is_manual && (
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <div className="flex items-center justify-between mb-2">
                                  <label className="block text-xs font-semibold text-gray-700">
                                    Certificates
                                    <span className="ml-1 font-normal text-gray-500">(verify competency)</span>
                                  </label>
                                  <Button
                                    type="button"
                                    onClick={() => handleAddWorkerCertification(worker.id)}
                                    size="sm"
                                    variant="outline"
                                  >
                                    <Plus className="w-3 h-3 mr-1" />
                                    Add Certificate
                                  </Button>
                                </div>
                                {(worker.certifications || []).length > 0 && (
                                  <div className="space-y-2">
                                    {(worker.certifications || []).map(cert => (
                                      <div key={cert.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                                        {(worker.required_certifications || []).length > 0 ? (
                                          <input
                                            list={`certs-${worker.id}`}
                                            value={cert.name}
                                            onChange={(e) => handleUpdateWorkerCertification(worker.id, cert.id, 'name', e.target.value)}
                                            className="md:col-span-4 px-3 py-2 text-sm border border-gray-300 rounded-lg"
                                            placeholder="Certification name"
                                          />
                                        ) : (
                                          <input
                                            value={cert.name}
                                            onChange={(e) => handleUpdateWorkerCertification(worker.id, cert.id, 'name', e.target.value)}
                                            className="md:col-span-4 px-3 py-2 text-sm border border-gray-300 rounded-lg"
                                            placeholder="Certification name"
                                          />
                                        )}
                                        <datalist id={`certs-${worker.id}`}>
                                          {(worker.required_certifications || []).map(rc => (
                                            <option key={rc} value={rc} />
                                          ))}
                                        </datalist>
                                        <input
                                          value={cert.certificate_number}
                                          onChange={(e) => handleUpdateWorkerCertification(worker.id, cert.id, 'certificate_number', e.target.value)}
                                          className="md:col-span-3 px-3 py-2 text-sm border border-gray-300 rounded-lg"
                                          placeholder="Certificate no."
                                        />
                                        <input
                                          value={cert.issuing_authority}
                                          onChange={(e) => handleUpdateWorkerCertification(worker.id, cert.id, 'issuing_authority', e.target.value)}
                                          className="md:col-span-3 px-3 py-2 text-sm border border-gray-300 rounded-lg"
                                          placeholder="Issuing authority"
                                        />
                                        <div className="md:col-span-2 flex items-center gap-1">
                                          <div className="w-full">
                                            <input
                                              type="date"
                                              value={cert.expiry_date}
                                              min={new Date().toISOString().slice(0, 10)}
                                              onChange={(e) => handleUpdateWorkerCertification(worker.id, cert.id, 'expiry_date', e.target.value)}
                                              className={`w-full px-2 py-2 text-sm border rounded-lg ${cert.expiry_date && cert.expiry_date < new Date().toISOString().slice(0, 10) ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                                              title="Expiry date (cannot be in the past)"
                                            />
                                            {cert.expiry_date && cert.expiry_date < new Date().toISOString().slice(0, 10) && (
                                              <p className="text-xs text-red-600 mt-1">Expiry date cannot be in the past.</p>
                                            )}
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => handleRemoveWorkerCertification(worker.id, cert.id)}
                                            className="text-red-600 hover:text-red-800 p-1"
                                            aria-label="Remove certificate"
                                          >
                                            <X className="w-4 h-4" />
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                    <p className="text-xs text-gray-500">Expired certificates (past expiry date) do not count towards competency.</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Hazards & Controls */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Hazards & Risk Controls</h3>
                  
                  {/* Link to HIRA */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Link to Existing HIRA (Optional)
                    </label>
                    <select
                      value={formData.linked_hira_id ? String(formData.linked_hira_id) : ''}
                      onChange={(e) => linkHIRA(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      <option value="">Select HIRA record to auto-populate hazards</option>
                      {hiraRecords.map(hira => (
                        <option key={hira.id} value={String(hira.id)}>
                          {hira.assessment_number || hira.hira_number || `HIRA-${hira.id?.slice(-6) || ''}`} | {hira.title || hira.activity || 'N/A'} | {hira.location || 'N/A'} | {hira.department || ''} {hira.overall_risk_level ? `| Risk: ${hira.overall_risk_level}` : ''}
                        </option>
                      ))}
                    </select>
                    {selectedHIRA && (
                      <div className="mt-2 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                        <div className="font-semibold">Linked to HIRA: {selectedHIRA.assessment_number || selectedHIRA.hira_number || selectedHIRA.title || selectedHIRA.activity}</div>
                        {selectedHIRA.description && (
                          <div className="mt-1 text-blue-700">Description: {selectedHIRA.description.substring(0, 100)}{selectedHIRA.description.length > 100 ? '...' : ''}</div>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Identified Hazards <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.hazards_identified.join('\n')}
                      onChange={(e) => setFormData({ ...formData, hazards_identified: e.target.value.split('\n') })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      rows="4"
                      placeholder="List each hazard on a new line"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Control Measures <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.control_measures.join('\n')}
                      onChange={(e) => setFormData({ ...formData, control_measures: e.target.value.split('\n') })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      rows="4"
                      placeholder="List each control measure on a new line"
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Safety Checklist */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  Safety Checklist{formData.permit_types && formData.permit_types.length > 0 ? ` — ${formData.permit_types.join(', ')}` : ''}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  <span className="text-red-500">*</span> All checklist items are mandatory. Check each item to confirm compliance.
                  If an item is not applicable, leave unchecked and provide remarks explaining why.
                </p>

                {formData.safety_checklist.length > 0 ? (
                  <div className="space-y-3">
                    {formData.safety_checklist.map((item, index, arr) => {
                      // Tri-state: an item is answered when it has a YES/NO/NA status
                      // (legacy items may only have `checked`). A NO needs remarks.
                      const st = item.status || (item.checked ? 'yes' : null);
                      const answered = !!st;
                      const isIncomplete = !answered || (st === 'no' && !item.remarks);
                      // Per-type section header when the group changes (combined permit).
                      const groupLabel = item.is_custom ? 'Custom Checkpoints' : (item.permit_type || '');
                      const prev = arr[index - 1];
                      const prevLabel = prev ? (prev.is_custom ? 'Custom Checkpoints' : (prev.permit_type || '')) : null;
                      const showHeader = groupLabel && groupLabel !== prevLabel;
                      return (
                        <React.Fragment key={item.id}>
                        {showHeader && (
                          <h4 className="text-sm font-bold text-gray-700 mt-4 pb-1 border-b border-gray-200">{groupLabel}</h4>
                        )}
                        <div
                          key={item.id}
                          className={`p-4 border rounded-lg ${
                            !answered ? 'border-red-300 bg-red-50' :
                            st === 'yes' ? 'border-green-300 bg-green-50' :
                            st === 'no' ? 'border-red-300 bg-red-50' : 'border-orange-300 bg-orange-50'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-1">
                              <label className="text-sm font-semibold text-gray-900">
                                {index + 1}. {item.question} <span className="text-red-500">*</span>
                                {item.is_custom && (
                                  <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">Custom</span>
                                )}
                              </label>
                              {/* YES / NO / NA tri-state */}
                              <div className="mt-2 inline-flex rounded-lg overflow-hidden border border-gray-300">
                                {[['yes', 'YES'], ['no', 'NO'], ['na', 'NA']].map(([val, lab]) => (
                                  <button
                                    key={val}
                                    type="button"
                                    onClick={() => handleChecklistStatus(item.id, val)}
                                    className={`px-4 py-1.5 text-xs font-bold border-r border-gray-300 last:border-r-0 ${
                                      st === val
                                        ? (val === 'yes' ? 'bg-green-600 text-white' : val === 'no' ? 'bg-red-600 text-white' : 'bg-gray-500 text-white')
                                        : 'bg-white text-gray-600 hover:bg-gray-50'
                                    }`}
                                  >
                                    {lab}
                                  </button>
                                ))}
                              </div>
                              <input
                                type="text"
                                value={item.remarks || ''}
                                onChange={(e) => handleChecklistChange(item.id, 'remarks', e.target.value)}
                                placeholder={st === 'no' ? 'Remarks required — explain the NO *' : 'Remarks (optional)'}
                                className={`mt-2 w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none ${
                                  st === 'no' && !item.remarks ? 'border-red-400 bg-red-50' : 'border-gray-300'
                                }`}
                              />
                              {st === 'no' && !item.remarks && (
                                <p className="text-xs text-red-600 mt-1">A "NO" answer must be explained in remarks.</p>
                              )}
                              {!answered && (
                                <p className="text-xs text-red-600 mt-1">Please answer YES, NO or NA.</p>
                              )}
                            </div>
                            <div className="flex-shrink-0 flex items-center gap-2">
                              {st === 'yes' ? (
                                <span className="text-green-600 text-sm font-medium">✓ Yes</span>
                              ) : st === 'no' ? (
                                <span className="text-red-600 text-sm font-medium">✗ No</span>
                              ) : st === 'na' ? (
                                <span className="text-gray-500 text-sm font-medium">N/A</span>
                              ) : (
                                <span className="text-red-600 text-sm font-medium">Pending</span>
                              )}
                              {item.is_custom && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveCheckpoint(item.id)}
                                  className="text-red-400 hover:text-red-600 ml-1"
                                  title="Remove custom checkpoint"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        </React.Fragment>
                      );
                    })}

                    {/* Add Custom Checkpoint */}
                    <div className="mt-4 p-4 border-2 border-dashed border-gray-300 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Add Additional Checkpoint</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={customCheckpoint}
                          onChange={(e) => setCustomCheckpoint(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCheckpoint())}
                          placeholder="Enter additional checkpoint description..."
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                        <Button
                          type="button"
                          onClick={handleAddCheckpoint}
                          disabled={!customCheckpoint.trim()}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add
                        </Button>
                      </div>
                    </div>

                    {/* Checklist Summary (tri-state aware) */}
                    <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                      {(() => {
                        const stOf = (i) => i.status || (i.checked ? 'yes' : null);
                        const yes = formData.safety_checklist.filter(i => stOf(i) === 'yes').length;
                        const no = formData.safety_checklist.filter(i => stOf(i) === 'no').length;
                        const na = formData.safety_checklist.filter(i => stOf(i) === 'na').length;
                        const pending = formData.safety_checklist.filter(i => !stOf(i) && !i.remarks).length;
                        return (
                          <div className="flex justify-between text-sm">
                            <span>Yes: <strong className="text-green-600">{yes}</strong></span>
                            <span>No: <strong className="text-red-600">{no}</strong></span>
                            <span>N/A: <strong className="text-orange-600">{na}</strong></span>
                            <span>Pending: <strong className="text-gray-700">{pending}</strong></span>
                            <span>Total: <strong>{formData.safety_checklist.length}</strong></span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    Select a permit type in Step 1 to load checklist
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Special Requirements */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <h3 className="text-lg font-bold text-gray-900">Special Requirements</h3>
                  <Button
                    type="button"
                    onClick={handleGenerateAI}
                    disabled={generatingJsa}
                    className="bg-blue-600 hover:bg-blue-700"
                    title="AI-draft the PPE and Job Safety Analysis from the permit type and work description"
                  >
                    {generatingJsa ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    Generate PPE &amp; JSA with AI
                  </Button>
                </div>
                <p className="text-xs text-gray-500 -mt-3">
                  AI drafts the PPE selection and JSA from the work description — a starting point to review and edit. Link a HIRA first to ground it on the assessed hazards.
                </p>

                {/* PPE / Safety Equipment */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-3">PPE / Safety Equipment Required</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {PPE_OPTIONS.map(opt => {
                      const mandatory = MANDATORY_PPE.includes(opt);
                      const on = mandatory || (formData.ppe_required || []).includes(opt);
                      return (
                        <label key={opt} className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm ${mandatory ? 'cursor-not-allowed' : 'cursor-pointer'} ${on ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:bg-gray-50'}`}>
                          <input type="checkbox" checked={on} disabled={mandatory} onChange={() => togglePpe(opt)} className="w-4 h-4" />
                          <span className="text-gray-800">{opt}</span>
                          {mandatory && <span className="ml-auto text-[10px] font-semibold text-blue-600 uppercase">Required</span>}
                        </label>
                      );
                    })}
                  </div>
                  <p className="mt-2 text-xs text-gray-500">Helmet and Safety Shoes are mandatory for all work permits.</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                    <input type="text" value={formData.ppe_fire_extinguisher_no} onChange={(e) => setFormData({ ...formData, ppe_fire_extinguisher_no: e.target.value })} className="px-3 py-2 text-sm border border-gray-300 rounded-lg" placeholder="Fire Extinguisher No." />
                    <input type="text" value={formData.ppe_locks_tags_no} onChange={(e) => setFormData({ ...formData, ppe_locks_tags_no: e.target.value })} className="px-3 py-2 text-sm border border-gray-300 rounded-lg" placeholder="Locks / Tags No." />
                    <input type="text" value={formData.ppe_other} onChange={(e) => setFormData({ ...formData, ppe_other: e.target.value })} className="px-3 py-2 text-sm border border-gray-300 rounded-lg" placeholder="Any other" />
                  </div>
                </div>

                {/* Insurance coverage (ESI / WC) */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-3">Insurance Coverage (ESI / WC / Other Policy)</h4>
                  <div className="flex items-center gap-4">
                    {[['yes', 'Yes', true], ['no', 'No', false]].map(([k, lab, val]) => (
                      <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="radio" name="esi_wc" checked={formData.esi_wc_covered === val} onChange={() => setFormData({ ...formData, esi_wc_covered: val })} className="w-4 h-4" />
                        {lab}
                      </label>
                    ))}
                  </div>
                  {formData.esi_wc_covered === false && (
                    <div className="mt-3 space-y-2">
                      <textarea
                        value={formData.esi_wc_approved_by}
                        onChange={(e) => setFormData({ ...formData, esi_wc_approved_by: e.target.value })}
                        rows="2"
                        className="w-full px-3 py-2 text-sm border border-amber-400 bg-amber-50 rounded-lg"
                        placeholder="Comments"
                      />
                      <p className="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2">
                        ⚠ Insurance coverage is marked "No". This permit's <strong>approval will be blocked</strong> until coverage is confirmed.
                      </p>
                    </div>
                  )}
                </div>

                {/* Tool Box Talk attendance — multiselect from the permit's workers */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-gray-900">Tool Box Talk — Attendance</h4>
                    {(formData.workers || []).length > 0 && (
                      <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(formData.workers || []).length > 0 && (formData.workers || []).every(isTbtWorkerSelected)}
                          onChange={(e) => setFormData(prev => {
                            if (e.target.checked) {
                              const existing = prev.tbt_attendees || [];
                              const toAdd = (prev.workers || [])
                                .filter(w => !existing.some(a => a.worker_id === w.id || a.name === w.employee_name))
                                .map(w => ({ id: Math.random().toString(36).substr(2, 9), worker_id: w.id, name: w.employee_name, job_profile: '', signature: '' }));
                              return { ...prev, tbt_attendees: [...existing, ...toAdd] };
                            }
                            return { ...prev, tbt_attendees: [] };
                          })}
                          className="w-4 h-4"
                        />
                        Select all
                      </label>
                    )}
                  </div>
                  {(formData.workers || []).length === 0 ? (
                    <p className="text-xs text-gray-500">Add workers in the Authorized Workers step first — Tool Box Talk attendees are chosen from that list.</p>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500 mb-1">Tick the workers who attended the Tool Box Talk.</p>
                      {(formData.workers || []).map(w => {
                        const attendee = (formData.tbt_attendees || []).find(a => a.worker_id === w.id || a.name === w.employee_name);
                        const checked = !!attendee;
                        return (
                          <div key={w.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                            <label className={`md:col-span-6 flex items-center gap-2 px-3 py-2 border rounded-lg text-sm cursor-pointer ${checked ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:bg-gray-50'}`}>
                              <input type="checkbox" checked={checked} onChange={() => toggleTbtWorker(w)} className="w-4 h-4" />
                              <span className="text-gray-800">
                                {w.employee_name}
                                {w.contractor_company ? <span className="text-gray-500"> — {w.contractor_company}</span> : (w.is_manual ? <span className="text-gray-500"> — External</span> : null)}
                              </span>
                            </label>
                            {checked && (
                              <input
                                value={attendee.job_profile || ''}
                                onChange={(e) => updateTbtAttendee(attendee.id, 'job_profile', e.target.value)}
                                className="md:col-span-6 px-3 py-2 text-sm border border-gray-300 rounded-lg"
                                placeholder="Job profile (optional)"
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Job Safety Analysis */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-gray-900">Job Safety Analysis (JSA)</h4>
                    <Button type="button" onClick={addJsaRow} size="sm" variant="outline"><Plus className="w-3 h-3 mr-1" />Add Row</Button>
                  </div>
                  {(formData.jsa || []).length > 0 && (
                    <div className="space-y-2">
                      {(formData.jsa || []).map(j => (
                        <div key={j.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start">
                          <input value={j.step} onChange={(e) => updateJsaRow(j.id, 'step', e.target.value)} className="md:col-span-4 px-3 py-2 text-sm border border-gray-300 rounded-lg" placeholder="Step (what to do)" />
                          <input value={j.hazard} onChange={(e) => updateJsaRow(j.id, 'hazard', e.target.value)} className="md:col-span-4 px-3 py-2 text-sm border border-gray-300 rounded-lg" placeholder="Hazard associated" />
                          <input value={j.control} onChange={(e) => updateJsaRow(j.id, 'control', e.target.value)} className="md:col-span-3 px-3 py-2 text-sm border border-gray-300 rounded-lg" placeholder="Control measure" />
                          <button type="button" onClick={() => removeJsaRow(j.id)} className="md:col-span-1 text-red-600 hover:text-red-800 p-1 justify-self-start" aria-label="Remove"><X className="w-4 h-4" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Authorization matrix */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-3">Authorization</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {AUTH_ROLES.map(role => (
                      <div key={role}>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">{role}</label>
                        <select value={getAuthName(role)} onChange={(e) => setAuthName(role, e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg">
                          {employeeNameOptions(getAuthName(role))}
                        </select>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Plant Head authorization is mandatory for high-risk roof work.</p>
                </div>

                {/* Shift-change hand-over (optional — revealed via toggle) */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-gray-900">Shift-Change Hand-over</h4>
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={handoverEnabled}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setHandoverEnabled(on);
                          // Clear any entered data when turning the section off so
                          // nothing stale is submitted.
                          if (!on) setFormData(prev => ({ ...prev, shift_handover: null }));
                        }}
                        className="w-4 h-4"
                      />
                      Applicable
                    </label>
                  </div>
                  {!handoverEnabled ? (
                    <p className="text-xs text-gray-500">Enable "Applicable" to record shift-change hand-over details (optional).</p>
                  ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-gray-600">Handed over by</p>
                      <select value={(formData.shift_handover || {}).handed_over_by || ''} onChange={(e) => setHandover('handed_over_by', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg">
                        {employeeNameOptions((formData.shift_handover || {}).handed_over_by || '')}
                      </select>
                      <PhoneInput
                        value={(formData.shift_handover || {}).handed_over_mobile || ''}
                        onChange={(v) => setHandover('handed_over_mobile', v)}
                        defaultCountry={defaultPhoneCountry()}
                        showCountrySelect={false}
                        placeholder="Mobile"
                        inputClassName="w-full px-3 py-2 text-sm border rounded-lg"
                      />
                    </div>
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-gray-600">Accepted by</p>
                      <select value={(formData.shift_handover || {}).accepted_by || ''} onChange={(e) => setHandover('accepted_by', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg">
                        {employeeNameOptions((formData.shift_handover || {}).accepted_by || '')}
                      </select>
                      <PhoneInput
                        value={(formData.shift_handover || {}).accepted_mobile || ''}
                        onChange={(v) => setHandover('accepted_mobile', v)}
                        defaultCountry={defaultPhoneCountry()}
                        showCountrySelect={false}
                        placeholder="Mobile"
                        inputClassName="w-full px-3 py-2 text-sm border rounded-lg"
                      />
                    </div>
                  </div>
                  )}
                </div>

                {/* Gas Testing */}
                {gasTestRequired && (
                  <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-bold text-blue-900">Gas Testing Required</h4>
                      <Button onClick={handleAddGasReading} size="sm" className="bg-blue-600 hover:bg-blue-700">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Reading
                      </Button>
                    </div>
                    
                    {formData.gas_test_readings.map((reading, index) => {
                      const warnings = getGasReadingWarnings(reading);
                      const hasWarnings = warnings.length > 0;

                      return (
                      <div key={reading.id} className={`mb-4 p-4 rounded-lg ${hasWarnings ? 'bg-red-50 border-2 border-red-300' : 'bg-white'}`}>
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="font-semibold text-gray-900">Reading #{index + 1}</h5>
                          {hasWarnings && (
                            <span className="flex items-center text-red-600 text-sm font-semibold">
                              <AlertTriangle className="w-4 h-4 mr-1" />
                              Unsafe Levels Detected
                            </span>
                          )}
                        </div>

                        {/* Warning messages */}
                        {hasWarnings && (
                          <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-lg">
                            <div className="flex items-start">
                              <AlertTriangle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="font-bold text-red-800 text-sm mb-1">⚠️ WARNING: Gas readings outside safe limits!</p>
                                <ul className="text-red-700 text-sm space-y-1">
                                  {warnings.map((warning, wIdx) => (
                                    <li key={wIdx}>• {warning}</li>
                                  ))}
                                </ul>
                                <p className="text-red-800 text-xs mt-2 font-semibold">
                                  DO NOT proceed with work until atmosphere is safe. Ventilate the area and re-test.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          <div className="col-span-2 md:col-span-1">
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Test Date & Time</label>
                            <DateTimePicker
                              value={reading.test_time}
                              onChange={(value) => handleUpdateGasReading(reading.id, 'test_time', value)}
                              className="w-full"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">O₂ Level (%) <span className="text-gray-500 font-normal">19.5-23.5%</span></label>
                            <input
                              type="number"
                              step="0.1"
                              value={reading.oxygen_level}
                              onChange={(e) => handleUpdateGasReading(reading.id, 'oxygen_level', e.target.value)}
                              className={`w-full px-3 py-2 text-sm border rounded-lg ${
                                isGasReadingFieldUnsafe(reading, 'oxygen_level')
                                  ? 'border-red-500 bg-red-50 text-red-900 focus:ring-red-500'
                                  : 'border-gray-300'
                              }`}
                              placeholder="19.5-23.5%"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">LEL (%) <span className="text-gray-500 font-normal">&lt;10%</span></label>
                            <input
                              type="number"
                              step="0.1"
                              value={reading.lel_level}
                              onChange={(e) => handleUpdateGasReading(reading.id, 'lel_level', e.target.value)}
                              className={`w-full px-3 py-2 text-sm border rounded-lg ${
                                isGasReadingFieldUnsafe(reading, 'lel_level')
                                  ? 'border-red-500 bg-red-50 text-red-900 focus:ring-red-500'
                                  : 'border-gray-300'
                              }`}
                              placeholder="<10%"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">H₂S (ppm) <span className="text-gray-500 font-normal">&lt;10 ppm</span></label>
                            <input
                              type="number"
                              step="0.1"
                              value={reading.h2s_level}
                              onChange={(e) => handleUpdateGasReading(reading.id, 'h2s_level', e.target.value)}
                              className={`w-full px-3 py-2 text-sm border rounded-lg ${
                                isGasReadingFieldUnsafe(reading, 'h2s_level')
                                  ? 'border-red-500 bg-red-50 text-red-900 focus:ring-red-500'
                                  : 'border-gray-300'
                              }`}
                              placeholder="<10 ppm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">CO (ppm) <span className="text-gray-500 font-normal">&lt;35 ppm</span></label>
                            <input
                              type="number"
                              step="0.1"
                              value={reading.co_level}
                              onChange={(e) => handleUpdateGasReading(reading.id, 'co_level', e.target.value)}
                              className={`w-full px-3 py-2 text-sm border rounded-lg ${
                                isGasReadingFieldUnsafe(reading, 'co_level')
                                  ? 'border-red-500 bg-red-50 text-red-900 focus:ring-red-500'
                                  : 'border-gray-300'
                              }`}
                              placeholder="<35 ppm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Tester Name</label>
                            <input
                              type="text"
                              value={reading.tester_name}
                              onChange={(e) => handleUpdateGasReading(reading.id, 'tester_name', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Remarks</label>
                            <input
                              type="text"
                              value={reading.remarks}
                              onChange={(e) => handleUpdateGasReading(reading.id, 'remarks', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            />
                          </div>
                        </div>

                        {/* Additional (custom) readings — user-defined label/value pairs */}
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-xs font-semibold text-gray-700">
                              Additional Readings
                              <span className="ml-1 font-normal text-gray-500">(e.g. SO₂, NH₃, temperature)</span>
                            </label>
                            <Button
                              type="button"
                              onClick={() => handleAddGasCustomField(reading.id)}
                              size="sm"
                              variant="outline"
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Add Field
                            </Button>
                          </div>
                          {(reading.custom_fields || []).length > 0 && (
                            <div className="space-y-2">
                              {(reading.custom_fields || []).map(field => (
                                <div key={field.id} className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={field.label}
                                    onChange={(e) => handleUpdateGasCustomField(reading.id, field.id, 'label', e.target.value)}
                                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg"
                                    placeholder="Label (e.g. SO₂ ppm)"
                                  />
                                  <input
                                    type="text"
                                    value={field.value}
                                    onChange={(e) => handleUpdateGasCustomField(reading.id, field.id, 'value', e.target.value)}
                                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg"
                                    placeholder="Value"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveGasCustomField(reading.id, field.id)}
                                    className="text-red-600 hover:text-red-800 p-1"
                                    aria-label="Remove field"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}

                {/* Isolation/LOTO */}
                {isolationRequired && (
                  <div className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-bold text-orange-900">Isolation & LOTO Required</h4>
                      <Button onClick={handleAddIsolationPoint} size="sm" className="bg-orange-600 hover:bg-orange-700">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Point
                      </Button>
                    </div>
                    
                    {formData.isolation_points.map((point, index) => (
                      <div key={point.id} className="mb-4 p-4 bg-white rounded-lg">
                        <h5 className="font-semibold text-gray-900 mb-3">Isolation Point #{index + 1}</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Equipment Tag</label>
                            <input
                              type="text"
                              value={point.equipment_tag}
                              onChange={(e) => handleUpdateIsolationPoint(point.id, 'equipment_tag', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Isolation Type</label>
                            <select
                              value={point.isolation_type}
                              onChange={(e) => handleUpdateIsolationPoint(point.id, 'isolation_type', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            >
                              <option>Electrical</option>
                              <option>Mechanical</option>
                              <option>Pneumatic</option>
                              <option>Hydraulic</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Lock Number</label>
                            <input
                              type="text"
                              value={point.lock_number}
                              onChange={(e) => handleUpdateIsolationPoint(point.id, 'lock_number', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Verified By</label>
                            <input
                              type="text"
                              value={point.isolation_verified_by}
                              onChange={(e) => handleUpdateIsolationPoint(point.id, 'isolation_verified_by', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            />
                          </div>
                        </div>

                        {/* Additional (custom) fields — user-defined label/value pairs */}
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-xs font-semibold text-gray-700">
                              Additional Fields
                              <span className="ml-1 font-normal text-gray-500">(e.g. isolation method, tag color)</span>
                            </label>
                            <Button
                              type="button"
                              onClick={() => handleAddIsolationCustomField(point.id)}
                              size="sm"
                              variant="outline"
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Add Field
                            </Button>
                          </div>
                          {(point.custom_fields || []).length > 0 && (
                            <div className="space-y-2">
                              {(point.custom_fields || []).map(field => (
                                <div key={field.id} className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={field.label}
                                    onChange={(e) => handleUpdateIsolationCustomField(point.id, field.id, 'label', e.target.value)}
                                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg"
                                    placeholder="Label"
                                  />
                                  <input
                                    type="text"
                                    value={field.value}
                                    onChange={(e) => handleUpdateIsolationCustomField(point.id, field.id, 'value', e.target.value)}
                                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg"
                                    placeholder="Value"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveIsolationCustomField(point.id, field.id)}
                                    className="text-red-600 hover:text-red-800 p-1"
                                    aria-label="Remove field"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Rescue Plan (for Confined Space) */}
                {rescuePlanRequired && (
                  <div className="border border-purple-200 rounded-lg p-4 bg-purple-50">
                    <h4 className="font-bold text-purple-900 mb-3">Rescue Plan</h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.rescue_plan_available}
                          onChange={(e) => setFormData({ ...formData, rescue_plan_available: e.target.checked })}
                          className="w-5 h-5 text-purple-600 rounded"
                        />
                        <label className="text-sm font-semibold text-gray-900">
                          Rescue plan and equipment available
                        </label>
                      </div>
                      <textarea
                        value={formData.rescue_plan_description}
                        onChange={(e) => setFormData({ ...formData, rescue_plan_description: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                        rows="3"
                        placeholder="Describe rescue procedures and equipment"
                      />
                    </div>
                  </div>
                )}

                {/* File Attachments */}
                <div className="border border-gray-200 rounded-lg p-4 bg-white">
                  <h4 className="font-bold text-gray-900 mb-3">File Attachments</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Attach supporting documents such as method statements, risk assessments, or other relevant files.
                  </p>
                  <FileUploadWidget
                    module="ptw"
                    referenceId={null}
                    maxFiles={5}
                    maxSizeMB={10}
                    allowedExtensions={['pdf', 'doc', 'docx', 'xls', 'xlsx']}
                    existingFiles={uploadedFiles}
                    onFilesChange={setUploadedFiles}
                    disabled={false}
                  />
                </div>

                {/* Emergency Contacts */}
                <div>
                  <h4 className="font-bold text-gray-900 mb-3">Emergency Contacts <span className="text-red-500">*</span></h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Contact Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.emergency_contact_name}
                        onChange={(e) => setFormData({ ...formData, emergency_contact_name: stripDigits(e.target.value) })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        placeholder="Emergency contact name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Contact Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        value={formData.emergency_contact_number}
                        onChange={(e) => setFormData({
                          ...formData,
                          emergency_contact_number: sanitizeMobile(e.target.value, contactRules.maxDigits),
                        })}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:outline-none ${
                          formData.emergency_contact_number
                          && !isValidContactNumber(formData.emergency_contact_number, contactCountry)
                            ? 'border-red-400 focus:ring-red-400'
                            : 'border-gray-300 focus:ring-blue-500'
                        }`}
                        placeholder={contactCountry === 'IN'
                          ? `${MOBILE_DIGITS}-digit mobile number`
                          : `Mobile number (${contactRules.hint}, without +${contactRules.dialCode})`}
                        inputMode="numeric"
                        maxLength={contactRules.maxDigits}
                        required
                      />
                      {/* The step gate below used to check only that this was
                          non-empty, so "9" reached the API as the number
                          somebody rings when the job goes wrong. Country-aware
                          (REG-13): India keeps exactly 10 digits. */}
                      {formData.emergency_contact_number
                        && !isValidContactNumber(formData.emergency_contact_number, contactCountry) && (
                        <p className="text-red-500 text-sm mt-1">
                          Enter a valid number — {contactRules.hint}
                          {' '}({formData.emergency_contact_number.length} entered)
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Mandatory AI verification acknowledgement — blocks submit until
                ticked whenever AI populated any field this session. */}
            {!isEditMode && currentStep === 4 && aiMeta && (
              <div className="mt-8">
                <AIVerifyAcknowledgement
                  checked={aiVerifiedAck}
                  onChange={setAiVerifiedAck}
                  unreviewedCount={aiFields.size}
                />
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center mt-8 pt-6 border-t">
              <Button
                onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                disabled={currentStep === 1}
                variant="outline"
              >
                Previous
              </Button>

              {!isEditMode && currentStep === 4 && (
                <AISubmitReminder aiFieldCount={aiFields.size} />
              )}

              {currentStep < 4 ? (
                <Button
                  onClick={() => setCurrentStep(currentStep + 1)}
                  disabled={
                    (currentStep === 1 && ((!formData.permit_types || formData.permit_types.length === 0) || !formData.work_description || !formData.location || !formData.work_start_date || !formData.work_end_date || new Date(formData.work_end_date) <= new Date(formData.work_start_date))) ||
                    (currentStep === 2 && (formData.hazards_identified.length === 0 || formData.control_measures.length === 0)) ||
                    (currentStep === 3 && formData.safety_checklist.length > 0 && formData.safety_checklist.some(item => {
                      const st = item.status || (item.checked ? 'yes' : null);
                      return !st || (st === 'no' && !item.remarks);
                    }))
                  }
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Next
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading || !formData.emergency_contact_name || !isValidContactNumber(formData.emergency_contact_number, contactCountry) || !formData.site_id || (!isEditMode && aiMeta && !aiVerifiedAck)}
                  title={!isEditMode && aiMeta && !aiVerifiedAck ? 'Confirm you have verified all AI-generated fields first' : undefined}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {isEditMode ? 'Update Permit' : 'Submit Permit'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
        )}
      </div>
    </div>
    </div>
  );
};

export default PTWCreate;
